import moment from 'moment'
import { mapState, mapActions, mapGetters } from "vuex";
import store from "@/store";
import { firebase, firestore, messaging } from "@/config/firebase";
import vuescroll from 'vuescroll';
import typeText from "./types/text";
import typeAddress from "./types/address";
import typeEvent from "./types/event";
import typeUpdate from "./types/update";
import typeInvoice from "./types/invoice";
import VueGoogleAutocomplete from "vue-google-autocomplete";
// import VuetifyGoogleAutocomplete from 'vuetify-google-autocomplete/dist/vuetify-google-autocomplete.common.js';
import GoogleMapLoader from './GoogleMapLoader';
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { PhotoSwipe } from 'v-photoswipe';
import LinkPrevue from 'link-prevue'
import axios from "axios";
import qr from "@/views/chat/qr.vue";
import * as linkify from 'linkifyjs';
import { loadImageFromVideo, isVideo, isImage, uploadFileToStorage } from "@/util/video_utils_v2";
import { getMp4Rotation } from "@/util/mp4_rotation";
import UserAvatar from '@/components/UserAvatar';
import config from "@/config/constants.js";

import Conversation from "@/views/chat/Conversation";
import SearchResult from "@/views/chat/SearchResult";
import ConversationHeader from "@/views/chat/ConversationHeader";
import SuggestionList from "@/views/chat/SuggestionList";
import UploadGallery from '@/views/chat/UploadGallery';
import Message from '@/views/chat/Message';
import invoiceModal from "./modals/invoice";
import invoiceReadyModal from "./modals/invoice_ready";
import SelectChatModal from "./modals/SelectChatModal";
import DeleteGroupDialog from "./modals/DeleteGroupDialog";
import EventAsistanceModal from "./modals/EventAsistanceModal";
import { v4 as uuid } from "uuid";
import { Picker, EmojiIndex } from 'emoji-mart-vue-fast';
import data from 'emoji-mart-vue-fast/data/all.json';
import 'emoji-mart-vue-fast/css/emoji-mart.css';

const emojiIndex = new EmojiIndex(data);
import SelectAddressModal from "./modals/SelectAddressModal";
import AddressRouteModal from "./modals/AddressRouteModal";
import IncomingCallModal from "./modals/IncomingCallModal";
import ActiveCallModal from "./modals/ActiveCallModal";
import Stories from "@/views/chat/Stories";
import SwitchAccount from "@/components/SwitchAccount";
import PrivacyModal from "./modals/PrivacyModal";
import AppVersionBadge from "@/components/AppVersionBadge.vue";

var pdfjsLib = require('pdfjs-dist-for-node');

export default {
  name: 'Chat',
  components: {
    Conversation,
    SearchResult,
    ConversationHeader,
    SuggestionList,
    vuescroll,
    'type-text': typeText,
    'type-address': typeAddress,
    'type-event': typeEvent,
    'type-update': typeUpdate,
    'type-invoice': typeInvoice,
    VueGoogleAutocomplete,
    ValidationObserver,
    ValidationProvider,
    'v-photoswipe': PhotoSwipe,
    qr,
    LinkPrevue,
    "invoice-modal": invoiceModal,
    "invoice-ready-modal": invoiceReadyModal,
    UploadGallery,
    UserAvatar,
    Message,
    SelectChatModal,
    Picker,
    EventAsistanceModal,
    DeleteGroupDialog,
    GoogleMapLoader,
    SelectAddressModal,
    AddressRouteModal,
    IncomingCallModal,
    ActiveCallModal,
    Stories,
    SwitchAccount,
    PrivacyModal,
    AppVersionBadge,
  },
  async mounted() {
    // Prevent browser from opening dropped files in a new window
    this._preventDrop = (e) => { e.preventDefault(); };
    document.addEventListener('dragover', this._preventDrop);
    document.addEventListener('drop', this._preventDrop);

    // Accept dropped files anywhere in the chat pane (capture phase so child
    // components can't swallow the drop), and accept pasted images from the
    // clipboard. While dragging, show a full-pane "Drop image" overlay
    // instead of opening the bottom upload bar. On drop, stage the files
    // into UploadGallery.
    const hasFiles = (e) =>
      !!(e.dataTransfer && e.dataTransfer.types &&
         Array.from(e.dataTransfer.types).includes('Files'));

    // Reset state helper — called from drop, dragend, and window blur so the
    // overlay never gets stuck if the browser swallows a dragleave.
    const resetDragState = () => {
      this._dragCounter = 0;
      this.fileDragActive = false;
    };

    this._onPaneDragEnter = (e) => {
      if (!this.selectedChatId) return;
      // Gate on hasFiles on ENTER only — this is the reliable signal that
      // a file drag is in progress. Non-file drags (text, existing chat
      // images dragged internally) won't have 'Files' in types so the
      // overlay stays hidden.
      if (!hasFiles(e)) return;
      e.preventDefault();
      this._dragCounter++;
      this.fileDragActive = true;
    };
    this._onPaneDragOver = (e) => {
      if (!this.selectedChatId) return;
      if (!hasFiles(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
      if (!this.fileDragActive) this.fileDragActive = true;
    };
    this._onPaneDragLeave = (e) => {
      if (!this.selectedChatId) return;
      // Do NOT gate on hasFiles — many browsers clear dataTransfer.types on
      // leave events, so the hasFiles check would fail and the counter
      // would never decrement, leaving the overlay stuck up. Instead, only
      // decrement if a drag is currently active.
      if (!this.fileDragActive) return;
      this._dragCounter = Math.max(0, this._dragCounter - 1);
      // If the drag left the window entirely (relatedTarget null), reset
      // immediately instead of waiting for the counter to unwind element
      // by element.
      if (!e.relatedTarget || this._dragCounter === 0) {
        resetDragState();
      }
    };
    this._onPaneDrop = async (e) => {
      if (!this.selectedChatId) return;
      resetDragState();
      if (!e.dataTransfer || !e.dataTransfer.files || !e.dataTransfer.files.length) return;
      e.preventDefault();
      e.stopPropagation();
      const files = Array.from(e.dataTransfer.files);
      const gallery = this.$refs.uploadGallery;
      if (!gallery) return;
      gallery.show = true;
      gallery.dragClass = '';
      for (const file of files) {
        const resource = await gallery.loadFileResource(file);
        await gallery.addFile(file, resource);
      }
    };
    // Belt-and-suspenders: if the drag is cancelled (ESC, dragged off to
    // another app, dropped on a non-target), these events are the last
    // guaranteed signal to clean up.
    this._onPaneDragEnd = () => resetDragState();
    this._onPaneWindowBlur = () => resetDragState();
    this._onPanePaste = async (e) => {
      if (!this.selectedChatId) return;
      const items = (e.clipboardData && e.clipboardData.items) || [];
      const files = [];
      for (const item of items) {
        if (item.kind === 'file') {
          const f = item.getAsFile();
          if (f) files.push(f);
        }
      }
      if (!files.length) return;
      // Don't hijack paste inside the text composer unless the clipboard is
      // an image (plain text paste should still go to the input).
      const hasNonImage = files.some(f => !/^image\//.test(f.type));
      const active = document.activeElement;
      const inEditable = active && (active.isContentEditable || /^(INPUT|TEXTAREA)$/.test(active.tagName));
      if (inEditable && hasNonImage) return;
      e.preventDefault();
      const gallery = this.$refs.uploadGallery;
      if (!gallery) return;
      gallery.show = true;
      gallery.dragClass = '';
      for (const file of files) {
        const resource = await gallery.loadFileResource(file);
        await gallery.addFile(file, resource);
      }
    };
    // Capture phase ensures child elements can't stopPropagation past us
    document.addEventListener('dragenter', this._onPaneDragEnter, true);
    document.addEventListener('dragover', this._onPaneDragOver, true);
    document.addEventListener('dragleave', this._onPaneDragLeave, true);
    document.addEventListener('drop', this._onPaneDrop, true);
    document.addEventListener('dragend', this._onPaneDragEnd, true);
    window.addEventListener('blur', this._onPaneWindowBlur);
    document.addEventListener('paste', this._onPanePaste, true);

    // Esc cancels reply/edit. One-line UX win that matches Slack/iMessage.
    this._onEscCancelReply = (e) => {
      if (e.key !== 'Escape') return;
      if (this.messageToReply || this.messageToEdit) {
        this.messageToReply = null;
        this.messageToEdit = null;
      }
    };
    document.addEventListener('keydown', this._onEscCancelReply);

    // When the tab becomes visible again after being hidden, if a chat is
    // currently open we retroactively mark its messages read. Without this,
    // a user who backgrounded the tab and came back would never have their
    // read receipts sent until the next message arrived.
    this._onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!this.selectedChatId) return;
      this.$store.dispatch('Chat/update_unread_counter', this.selectedChatId);
    };
    document.addEventListener('visibilitychange', this._onVisibilityChange);

    // Click-outside handler for emoji picker
    document.addEventListener('click', this.onDocumentClick);

    // Start listening for incoming calls
    this.$store.dispatch('Call/startCallSignalListener');

    // Restore session from localStorage on page refresh
    if (this.hasToken && !this.owner_id) {
      const storedOwnerId = localStorage.getItem('owner_id');
      const storedAuthId = localStorage.getItem('auth_id');
      const storedTenantId = localStorage.getItem('tenant_id');
      const storedUserDoc = localStorage.getItem('user_doc');
      const storedDeviceId = localStorage.getItem('device_id');
      if (storedOwnerId && storedAuthId && storedTenantId) {
        axios.defaults.headers.common['Authorization'] = storedAuthId;
        if (storedTenantId) this.$store.commit('Chat/tenant', storedTenantId);
        if (storedUserDoc) this.$store.commit('Chat/user_doc', storedUserDoc);
        if (storedDeviceId) this.$store.commit('Chat/device_id', storedDeviceId);
        this.$store.commit('Chat/owner_id', storedOwnerId);
        // owner_id watcher triggers init() → logged() → full session restore
      } else {
        // No restorable session (partial/stale localStorage fragments).
        // There's nothing a timeout can recover — clear the stale keys
        // immediately and dispatch web_token so the QR renders right away.
        // The previous 5-second setTimeout was the cause of the long
        // empty-state delay on /chat page load.
        localStorage.removeItem('auth_id');
        localStorage.removeItem('WA_id');
        localStorage.removeItem('token');
        localStorage.removeItem('owner_id');
        localStorage.removeItem('tenant_id');
        localStorage.removeItem('user_doc');
        localStorage.removeItem('device_id');
        this.tokenCleared = true;
        this.$store.dispatch('Chat/web_token');
      }
    }

    // Listen for notification clicks forwarded from service worker
    this._swMessageHandler = (event) => {
      if (event.data && event.data.conversation_id) {
        this.openConversationById(event.data.conversation_id);
      }
    };
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', this._swMessageHandler);
    }

    // Listen for Electron native notification clicks + check for updates
    if (window.electronAPI) {
      window.electronAPI.onNotificationClick((data) => {
        if (data && data.conversation_id) {
          this.openConversationById(data.conversation_id);
        }
      });

      // Check for app updates on launch
      if (window.electronAPI.checkForUpdate) {
        window.electronAPI.checkForUpdate().then((result) => {
          if (result && result.updateAvailable) {
            this.updateAvailable = true;
            this.remoteVersion = result.remoteVersion;
          }
        }).catch(() => {});
      }
    }

    // Handle deep link from notification click (new tab opened with ?open_conversation=)
    const urlParams = new URLSearchParams(window.location.search);
    const openConvId = urlParams.get('open_conversation');
    if (openConvId) {
      this._pendingOpenConversation = openConvId;
      // Clean URL without reloading
      window.history.replaceState({}, '', window.location.pathname);
    }
  },
  beforeDestroy() {
    if (this._authTimeout) clearTimeout(this._authTimeout);
    this.unsuscribe();
    document.removeEventListener('click', this.onDocumentClick);
    if (this._preventDrop) {
      document.removeEventListener('dragover', this._preventDrop);
      document.removeEventListener('drop', this._preventDrop);
    }
    if (this._onPaneDragEnter) document.removeEventListener('dragenter', this._onPaneDragEnter, true);
    if (this._onPaneDragOver) document.removeEventListener('dragover', this._onPaneDragOver, true);
    if (this._onPaneDragLeave) document.removeEventListener('dragleave', this._onPaneDragLeave, true);
    if (this._onPaneDrop) document.removeEventListener('drop', this._onPaneDrop, true);
    if (this._onPaneDragEnd) document.removeEventListener('dragend', this._onPaneDragEnd, true);
    if (this._onPaneWindowBlur) window.removeEventListener('blur', this._onPaneWindowBlur);
    if (this._onPanePaste) document.removeEventListener('paste', this._onPanePaste, true);
    if (this._onVisibilityChange) document.removeEventListener('visibilitychange', this._onVisibilityChange);
    if (this._onEscCancelReply) document.removeEventListener('keydown', this._onEscCancelReply);
    if (this._typingInterval) {
      clearInterval(this._typingInterval);
    }
    if (this._tokenRefreshInterval) {
      clearInterval(this._tokenRefreshInterval);
    }
    this.teardownMessageListener();
    // Remove scroll listeners from messages panel
    const messagesWindow = document.querySelector(".messages-scroll.__vuescroll > .__panel");
    if (messagesWindow) {
      messagesWindow.removeEventListener('mousewheel', this.handleScrollMessages);
      messagesWindow.removeEventListener('onmousewheel', this.handleScrollMessages);
      messagesWindow.removeEventListener('DOMMouseScroll', this.handleScrollMessages);
    }
    // Remove EventBus listeners
    if (this._reloadHandler) {
      window.EventBus.$off('reload', this._reloadHandler);
    }
    if (this._sendFailedHandler) {
      window.EventBus.$off('message-send-failed', this._sendFailedHandler);
    }
    if (this._updateMessagesHandler) {
      window.EventBus.$off('onUpdateMessages', this._updateMessagesHandler);
    }
    // Remove service worker message listeners
    if (this._swMessageHandler && navigator.serviceWorker) {
      navigator.serviceWorker.removeEventListener('message', this._swMessageHandler);
    }
    if (this._initSwHandler && navigator.serviceWorker) {
      navigator.serviceWorker.removeEventListener('message', this._initSwHandler);
    }
    this.$store.dispatch('Call/stopCallSignalListener');
  },
  watch: {
    searchTextMessages(val) {
      if (!val || !val.trim()) {
        // Si el texto de bsqueda se borra, ocultar resultados
        setTimeout(() => {
          if (!this.searchTextMessages || !this.searchTextMessages.trim()) {
            this.showSearchResults = false;
            this.currentResultIndex = 0;
          }
        }, 100);
      }
    },
    owner_id(val) {
      if (val) {
        if (this._authTimeout) { clearTimeout(this._authTimeout); this._authTimeout = null; }
        this.unsuscribe();
        this.init();
      } else {
        this.unsuscribe();
      }
    },
    selected() {
      const self = this;
      self.search = '';
      (self.selected.length > 0) ? self.valid = true : self.valid = false;
    },
    uploadValue(update) {
      const self = this;
      if (update === 100) {
        setTimeout(() => {
          self.overlay = false;
          self.$store.commit("Chat/status_file", '');
        }, 3000);
      }
    },
    searchChats(val) {
      if (val) {
        this.showResultSearch = true;
        window.search_status = "canceled";
        if (this.searchTimeOut) {
          clearTimeout(this.searchTimeOut);
        }
        this.searchTimeOut = setTimeout(() => {
          window.search_status = "";
          this.$store.dispatch("Chat/search", { search: val });
        }, 250);
      } else {
        this.showResultSearch = false;
      }
    },
    selectedChatId(val) {
      if (val) {
        this.getUserTyping();
        this.setupMessageListener(val);
      } else {
        this.teardownMessageListener();
      }
    },
    selectedChat(val) {
      if (val) {
        // Resetear filtros cuando cambia el chat
        this.selectedFilters = [];
        this.searchTextMessages = "";
        this.calculateUnreadCountersPosition();
      }
    },
    connectionStatus(newStatus, oldStatus) {
      if (newStatus === 'online' && oldStatus !== 'online') {
        // Connection restored — retry if init had failed
        if (this.initError) {
          this.retryInit();
        }
        // Re-fetch messages for the currently selected conversation
        if (this.selectedChatId && this.xmessages.length === 0) {
          const chat = this.conversations.find(x => x.chat_id === this.selectedChatId);
          if (chat) {
            this.$store.dispatch('Chat/messages', { chat_id: this.selectedChatId, chat });
          }
        }
      }
    },
    totalUnread(newCount) {
      // Tab title badge (moved here from the computed — side effects don't
      // belong in a computed getter; this watcher fires only when it changes).
      document.title = newCount > 0 ? `(${newCount}) Kiey` : 'Kiey';
      // Update Electron dock badge
      if (window.electronAPI) {
        window.electronAPI.setBadgeCount(newCount);
      }
      // Skip sound — only update badge/title silently
    }
  },
  filters: {
    retrieve_url_filter(id, list) {
      let file = list.find(f => f.id === id);
      if (file) {
        return file['url'];
      }
    },
    retrieve_url__preview_filter(id, list) {
      let file = list.find(f => f.id === id);
      if (file) {
        return file['media_preview_url'];
      }
    },
    link_directions(ontheway, lat, long) {
      var origin = "&origin=" + lat + "," + long;
      var destination = "&destination=" + ontheway.latitude + "," + ontheway.longitude;
      var url = "https://www.google.com/maps/dir/?api=1" + origin + destination;
      return url;
    },
    link_address(address) {
      return "https://www.google.com/maps?q=" + address.latitude + "," + address.longitude + "&z=17";
    },
    format_event_date(value) {
      if (value) {
        return moment(value).format('MM-DD-YY hh:mm a');
      }
    },
    formatTime(value) {
      if (!value) return '';
      var msgTime = moment(value);
      if (!msgTime.isValid()) return '';
      var now = moment();
      var diffMinutes = now.diff(msgTime, 'minutes');
      if (diffMinutes < 1) return 'Now';
      if (diffMinutes < 5) return diffMinutes + 'm';
      return msgTime.format('LT');
    },
    formatDate(value) {
      if (!value) return '';
      const d = moment(new Date(value));
      if (!d.isValid()) return '';
      if (d.isSame(moment(), 'day')) return 'Today';
      if (d.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday';
      if (d.isSame(moment(), 'year')) return d.format('ddd, MMM DD');
      return d.format('MMM DD, YYYY');
    },
  },
  computed: {
    ...mapState({
      preloading: state => state.Chat.preloading,
      search_results: state => state.Chat.search_results,
      status: state => state.Chat.status,
      owner_id: state => state.Chat.owner_id,
      device_id: state => state.Chat.device_id,
      tenant_id: state => state.Chat.tenant_id,
      conversations: state => state.Chat.conversations,
      xmessages: state => state.Chat.messages,
      mediaFilesChat: state => state.Chat.mediaFilesChat,
      status_msg: state => state.Chat.status_msg,
      status_file: state => state.Chat.status_file,
      status_users: state => state.Chat.status_users,
      status_conversations: state => state.Chat.status_conversations,
      status_chat: state => state.Chat.status_chat,
      status_chat_get: state => state.Chat.status_chat,
      status_contact: state => state.Chat.status_contact,
      status_retrieve: state => state.Chat.status_retrieve,
      items: state => state.Chat.users,
      unread_message: state => state.Chat.unread_message,
      all_messages: state => state.Chat.all_messages,
      chat_ids: state => state.Chat.chat_ids,
      logged: state => state.Chat.logged,
      countrySelected: state => state.Chat.countrySelected,
      search_status: state => state.Chat.search_status,
      uxDocs: state => state.Chat.uxDocs,
      tenant_company_name: state => state.Chat.tenant_company_name,
      initError: state => state.Chat.initError,
      connectionStatus: state => state.Connection.status
    }),
    hasToken() {
      if (this.tokenCleared) return false;
      if (this.$store.state.Chat.token) return true;
      return !!(localStorage.getItem('auth_id') || localStorage.getItem('WA_id') || localStorage.getItem('token'));
    },
    // Whether there's a restorable login session in localStorage. Used to gate
    // the QR vs the loading screen. Deliberately checks ONLY the real session
    // keys (mirrors the restore condition in mounted) and NOT the QR web-agent
    // token (WA_id / Chat.token) — the QR page fetches that token to draw the
    // code, so gating on it would hide the QR the instant it loads.
    hasStoredSession() {
      return !!(
        localStorage.getItem('owner_id') &&
        localStorage.getItem('auth_id') &&
        localStorage.getItem('tenant_id')
      );
    },
    selectedChat() {
      const self = this;
      if (self.selectedChatId) {
        return this.conversations.find(x => x.chat_id === self.selectedChatId) || {};
      }
      return {};
    },
    isElectron() {
      return navigator.userAgent.includes('Electron');
    },
    ...mapGetters({ 'getToken': ['Chat/getToken'], 'isOnline': 'Connection/isOnline' }),
    allSelected() {
      return this.selected.length === this.items.length
    },
    totalUnread() {
      const self = this;
      let count = 0;
      const prefix = '/tenants/' + self.tenant_id + '/users/';
      for (const conv of self.conversations) {
        if (!conv.unread_counters) continue;
        // Try owner_id directly
        let unread = conv.unread_counters[self.owner_id];
        if (unread === undefined) {
          // Fallback: find my user_id from participant_ids (handles ID mismatch)
          const otherIds = (conv.participants || []).filter(p => p.user_id !== self.owner_id).map(p => p.user_id);
          const ids = (conv.participant_ids || []).map(p => p.replace(prefix, ''));
          const myId = ids.find(id => !otherIds.includes(id));
          unread = myId ? (conv.unread_counters[myId] || 0) : 0;
        }
        count += unread;
      }
      return count;
    },
    xconversations() {
      const self = this;
      const seen = new Set();
      return self.conversations
        .map(x => {
          const sticky = self.stickyUnread[x.chat_id];
          // Display the MAX of sticky and live Firestore count. Sticky only
          // clears when user clicks the chat in this tab; live count may
          // drop from other devices reading the chat — we ignore that.
          const live = x.unread_messages_count || 0;
          const displayCount = Math.max(live, sticky || 0);
          return {
            ...x,
            selected: x.chat_id === self.selectedChatId,
            unread_messages_count: displayCount
          };
        })
        .filter(x => {
          // Allow groups and chats with titles through
          if (x.is_group || x.title) return true;
          // For 1:1 chats, dedup by the other participant's user_id
          const otherParticipant = (x.participant_ids || [])
            .find(pid => !pid.includes(self.owner_id));
          if (!otherParticipant) return true;
          if (seen.has(otherParticipant)) return false;
          seen.add(otherParticipant);
          return true;
        })
        .sort((a, b) => {
          const extractDate = (conv) => {
            const d = conv.last_message && conv.last_message.date;
            if (!d) return new Date(0);
            if (d.seconds) return new Date(d.seconds * 1000);
            if (typeof d.toDate === 'function') return d.toDate();
            const parsed = new Date(d);
            return isNaN(parsed.getTime()) ? new Date(0) : parsed;
          };
          return extractDate(b) - extractDate(a);
        });
    },
    xparticipants() {
      const search = this.search.toLowerCase();
      let list;

      // Remove participant mode: show current chat participants (except yourself)
      if (this.removeParticipantMode && this.selectedChat) {
        const participants = this.selectedChat.participants || [];
        list = participants
          .filter(p => p.user_id !== this.owner_id)
          .map(p => {
            const uxDoc = this.uxDocs[p.user_id] || {};
            return {
              ...uxDoc,
              first_name: p.first_name || uxDoc.first_name || '',
              last_name: p.last_name || uxDoc.last_name || '',
              phone_number: p.phone_number || uxDoc.phone_number || '',
              owner_id: p.user_id,
              text: ((p.first_name || '') + ' ' + (p.last_name || '')).toLowerCase(),
              category: uxDoc.category || 'other',
              categoryOrder: uxDoc.categoryOrder || 4,
            };
          });
        if (search) {
          list = list.filter(x => x.text.indexOf(search) > -1);
        }
        return list;
      }

      if (!search) {
        list = [...this.items];
      } else {
        list = this.items.filter(x => x.text && x.text.toLowerCase().indexOf(search) > -1);
      }

      // Sort by category order (agents → vendors → clients), active first within each
      list.sort((a, b) => (a.categoryOrder || 4) - (b.categoryOrder || 4));
      const catMap = new Map();
      list.forEach(item => {
        const cat = (item.category || '').toLowerCase();
        if (!catMap.has(cat)) catMap.set(cat, { active: [], inactive: [] });
        if (item.status === 'inactive') {
          catMap.get(cat).inactive.push(item);
        } else {
          catMap.get(cat).active.push(item);
        }
      });
      let sorted = [];
      catMap.forEach(({ active, inactive }) => {
        sorted.push(...active, ...inactive);
      });
      if (this.addParticipantMode && this.selectedChat && this.selectedChat.participant_ids) {
        const existingIds = new Set(
          (this.selectedChat.participant_ids || []).map(pid => pid.split('/').pop())
        );
        sorted = sorted.filter(item => !existingIds.has(item.owner_id));
      }
      return sorted;
    },
    selections() {
      const selections = [];
      const self = this;
      self.settings = [];
      for (const selection of this.selected) {
        selections.push(selection);
        self.settings.push(selection);
      }
      return selections
    },
    xmessagesFiltered() {
      // Mostrar todos los mensajes sin filtros
      // Los filtros se usarn para buscar y mostrar resultados en modal separado
      return this.xmessages || [];
    },

    searchResults() {
      // Busca en TODOS los mensajes de la conversacin (no solo los visibles)
      // Utiliza state.Chat.messages que contiene el historial completo
      const allMessages = this.xmessages || [];

      if (allMessages.length === 0) {
        return [];
      }

      let results = [...allMessages];

      // Filtrar por tipo de mensaje si hay filtros seleccionados
      if (this.selectedFilters && this.selectedFilters.length > 0) {
        results = results.filter(x => {
          const typeMatch = this.selectedFilters.includes(x.type);
          const mediaMatch = x.media && this.selectedFilters.includes(x.media.file_subtype);
          return typeMatch || mediaMatch;
        });
      }

      // Filtrar por texto de bsqueda si hay
      if (this.searchTextMessages && this.searchTextMessages.trim()) {
        const searchLower = this.searchTextMessages.toLowerCase().trim();

        results = results.filter(x => {
          // Funcin para limpiar HTML y obtener texto plano
          const stripHtml = (html) => {
            if (!html) return '';
            return html.replace(/<[^>]*>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'");
          };

          // Normalizar: convertir a minsculas y limpiar espacios mltiples
          const normalize = (str = '') =>
            str
              .toLowerCase()
              .replace(/\s+/g, ' ')
              .trim();

          // Buscar en text (limpiando HTML si existe)
          const cleanText = normalize(stripHtml(x.text || ''));
          const searchNormalized = normalize(searchLower);
          const textMatch = cleanText.includes(searchNormalized);

          // Buscar en sender_name
          const senderMatch = x.sender_name && normalize(x.sender_name).includes(searchNormalized);
          // Buscar en first_name y last_name
          const firstNameMatch = x.first_name && normalize(x.first_name).includes(searchNormalized);
          const lastNameMatch = x.last_name && normalize(x.last_name).includes(searchNormalized);

          const result = textMatch || senderMatch || firstNameMatch || lastNameMatch;
          return result;
        });
      }

      // Si hay filtros O bsqueda de texto, retornar resultados
      if ((this.selectedFilters && this.selectedFilters.length > 0) ||
        (this.searchTextMessages && this.searchTextMessages.trim())) {
        return results;
      }

      return [];
    }
  },
  data: () => {
    return {
      allMessagesLoadedForChat: false,
      selectedFilters: [],
      searchTextMessages: "",
      showSearchResults: false,
      currentResultIndex: 0,
      scrollingToMessage: false,
      emojiIndex: emojiIndex,
      previousStartAt: "",
      lastMessageSentByMe: false,
      routeSelected: null,
      event: { chat_id: '', address: '', url: '', latitude: null, longitude: null },
      overlay_ontheway: false,
      userTypingText: "",
      currentDate: new Date(),
      intervalUserWriting: null,
      showDeleteGroupDialog: false,
      showEnableNotifications: false,
      updateAvailable: false,
      remoteVersion: '',
      errorToast: "",
      errorToastTimer: null,
      fileDragActive: false,
      _dragCounter: 0,
      // Slack-style "New messages" divider state. Captured when you open a
      // chat with existing unread messages and frozen there while you view
      // the chat — it does NOT follow new arrivals. It clears when you
      // navigate away (selectedChatId changes) or re-open the same chat
      // (check_selected snapshots a fresh, now-zero count).
      unreadAnchorCount: 0,
      unreadAnchorMessageId: null,
      // Count of inbound messages that arrived while the user was scrolled
      // up (not at the bottom of the thread). Surfaces as a badge on the
      // scroll-to-bottom button; clears when user reaches bottom or
      // selects a different chat.
      unreadBelow: 0,
      // Per-tab sticky badge counts. Firestore unread_counters is the source
      // of truth for INCREASES (new messages bump the badge), but decreases
      // are only honored when the user explicitly opens that chat in THIS
      // tab — clicking selectedConversation zeros the entry here. Prevents
      // cross-device/cross-session read sync from silently clearing badges
      // on this web tab before the user has actually seen the message.
      stickyUnread: {},
      messagePositionConversation: {},
      reloadMessagesAvatar: false,
      messageEventSelected: null,
      showCancelOptionsForEvent: false,
      showEmojiPicker: false,
      emojiPickerStyle: {},
      addParticipantMode: false,
      removeParticipantMode: false,
      selectedMessage: null,
      showSelectChatModal: false,
      showEventAsistanceModal: false,
      messageToReply: null,
      messageToEdit: null,
      currentWord: "",
      showSuggestion: false,
      showProfileMenu: false,
      showPrivacyModal: false,
      tokenCleared: false,
      suggestionList: 0,
      apiUrl: config.api_url + "url_metadata",
      selectedChatId: "",
      messageListenerUnsub: null,
      previewImageData: {
        isOpen: false,
        items: [
          {
            src: '',
            w: 0,
            h: 0,
          }
        ],
        options: {
          index: 0,
          closeEl: true,
          captionEl: false,
          fullscreenEl: false,
          zoomEl: false,
          shareEl: false,
          counterEl: false,
          arrowEl: false,
          preloaderEl: true,
        }
      },
      dragFileClass: "",
      suscriptions: [],
      currentToken: '',
      new_chat_mode: '',
      showResultSearch: false,
      uploadIconURL: false,
      sizeParticipants: false,
      imageData: null,
      picture: null,
      uploadValue: 0,
      infoRoute: {
        start: {
          latitude: null,
          longitude: null,
        }
      },
      url: null,
      absolute: true,
      opacity: 1,
      overlay: false,
      overlay_map: false,
      image: null,
      message_send: false,
      last_message: null,
      settings: [],
      classArray: [],
      typeSelected: 'text',
      address: '',
      ops: {
        vuescroll: {
          detectResize: true,
          native: true
        },
        scrollPanel: {
          initialScrollY: 0,
          initialScrollX: false,
          scrollingX: false,
          scrollingY: true,
        },
        rail: {
          size: '3px',
        },
        bar: {
          keepShow: true,
          background: '#c1c1c1',
          size: '4px',
        }
      },
      displayOptionsText: false,
      conversationTitle: '',
      search: '',
      searchChats: '',
      searchTimeOut: null,
      selected: [],
      drawerParticipants: false,
      drawerGroup: false,
      drawerGroupTitle: false,
      valid: false,
      messageText: '',
      messageContent: null,
      drafts: {}, // per-chat unsent composer text, keyed by chat_id
      disabled: true,
      target: '_blank',
      chatMenu: [
        { title: 'New Chat' },
        { title: 'New Group' },
      ],
      url_temp_invoice: null,
      sender_profile_picture_invoice: null,
      showInvoiceModal: false,
      showInvoiceReadyModal: false,
      text_hash: '#',
      bottom: false,
      bottomLoading: false,
      messagesButtomOfPage: true,
      ignoreMessagesScrollEvent: true
    }
  },
  created() {
    const self = this;
    const firebaseConfig = config.firebaseConfig;

    // Only tick while a chat is open — no need to recompute typing + bump a
    // reactive date every second when nothing's on screen (idle CPU + re-renders).
    this._typingInterval = setInterval(() => {
      if (!self.selectedChatId) return;
      this.currentDate = new Date();
      this.getUserTyping();
    }, 1000);

    if (!firebase.apps.length) {
      firebase.initializeApp(firebaseConfig);
    } else {
      firebase.app();
    }

    this._reloadHandler = function () {
      // Clean up old tenant's Firestore listeners before re-initializing
      this.unsuscribe();
      this.teardownMessageListener();
      // Drop cached messages so a different tenant can't flash the prior
      // tenant's threads before the fresh fetch lands.
      this.$store.state.Chat.messagesCache = {};
      this.$store.state.Chat.messages = [];
      self.selectedChatId = "";
      self.selectedConversation({});
      try {
        this.$refs.uploadGallery.hideModal();
      } catch (error) {
        //
      }
      this.init();
    }.bind(this);
    window.EventBus.$on('reload', this._reloadHandler);

    this._sendFailedHandler = function () { self.showErrorToast("Message failed to send. Tap the message to retry."); };
    window.EventBus.$on('message-send-failed', this._sendFailedHandler);

    this._updateMessagesHandler = function () {
      if (this.messagesButtomOfPage) {
        if (!self.scrollingToMessage) {
          self.scrollable("vs", "100%");
        }
      }
    }.bind(this);
    window.EventBus.$on('onUpdateMessages', this._updateMessagesHandler);
  },
  methods: {
    async retryInit() {
      this.$store.commit("Chat/preloading", true);
      try {
        this.$store.commit("Chat/initError", false);
        await this.$store.dispatch('Chat/get_users_global');
        await this.addConversations();
      } catch (error) {
        console.error('[Chat] Retry init failed:', error);
        this.$store.commit("Chat/initError", true);
      }
      this.selectedConversation({});
      this.$store.commit("Chat/preloading", false);
    },
    onRemoveParticipant(participant) {
      const name = ((participant.first_name || '') + ' ' + (participant.last_name || '')).trim();
      if (confirm('Remove ' + name + ' from this conversation?')) {
        this.$store.dispatch('Chat/remove_participant', {
          chat_id: this.selectedChatId,
          user_id: participant.user_id,
        });
      }
    },
    async onInitiateCall(callType) {
      if (!this.selectedChat || !this.selectedChatId) return;
      // Get the other participant's user ID for 1:1 chats
      const participants = this.selectedChat.participant_ids || [];
      const otherPath = participants.find(p => !p.includes(this.owner_id));
      if (!otherPath) return;
      const calleeId = otherPath.split('/').pop();
      try {
        await this.$store.dispatch('Call/initiateCall', {
          calleeId,
          chatId: this.selectedChatId,
          callType,
        });
      } catch (err) {
        console.log('[Chat] Call initiate failed:', err.message);
      }
    },
    clearSearch() {
      this.selectedFilters = [];
      this.searchTextMessages = "";
      this.showSearchResults = false;
      this.currentResultIndex = 0;

      // Tambin resetear en ConversationHeader si es necesario
      if (this.$refs.conversationHeader) {
        this.$refs.conversationHeader.resetSearch();
      }
    },
    onSelectedFilters(data) {

      // Soportar ambos formatos: array antiguo y objeto nuevo
      if (Array.isArray(data)) {
        this.selectedFilters = data;
        this.searchTextMessages = "";
      } else if (data && typeof data === 'object') {
        this.selectedFilters = data.filters || [];
        this.searchTextMessages = data.searchText || "";
      } else {
        console.warn("Formato de filtro no reconocido:", data);
        return;
      }


      // Si hay filtros o bsqueda, mostrar div de resultados
      if (this.selectedFilters.length > 0 || (this.searchTextMessages && this.searchTextMessages.trim())) {
        this.showSearchResults = true;
        this.currentResultIndex = 0;
        // Auto-scroll al primer resultado
        if (this.searchResults.length > 0) {
          setTimeout(() => {
            this.scrollToResult(0);
          }, 300);
        }
      } else {
        // IMPORTANTE: Resetear todo cuando no hay bsqueda ni filtros
        this.showSearchResults = false;
        this.currentResultIndex = 0;
        this.selectedFilters = [];
        this.searchTextMessages = "";
      }
    },

    previousResult() {
      const self = this;
      if (self.currentResultIndex > 0) {
        self.currentResultIndex--;
        self.scrollToResult(self.currentResultIndex);
      }
    },

    nextResult() {
      const self = this;
      if (self.currentResultIndex < self.searchResults.length - 1) {
        self.currentResultIndex++;
        self.scrollToResult(self.currentResultIndex);
      }
    },

    scrollToResult(index) {
      const self = this;
      if (!self.searchResults || !self.searchResults[index]) return;

      const messageId = self.searchResults[index].message_id;

      setTimeout(() => {
        try {
          const messageElement = document.getElementById(messageId);
          if (messageElement) {
            const scrollPanel = document.querySelector(".messages-scroll.__vuescroll > .__panel");
            if (scrollPanel) {
              const offsetTop = messageElement.offsetTop;
              scrollPanel.scrollTop = offsetTop - 100;
            }
          }
        } catch (e) {
          console.error(e);
        }
      }, 100);
    },
    calculateUnreadCountersPosition() {
      const self = this;
      let conversation = self.selectedChat;
      if (!conversation || !conversation.participants || !conversation.unread_counters) return;
      let participants = conversation.participants.filter(item => item.user_id != self.owner_id);
      let messagePosition = {};
      for (const participant of participants) {
        let index = (self.xmessages.length - (conversation.unread_counters[participant.user_id] || 0)) - 1;
        if (messagePosition[index] === undefined) {
          messagePosition[index] = [{
            id: participant.user_id,
            first_name: participant.first_name,
            last_name: participant.last_name
          }];
        } else {
          messagePosition[index].push({
            id: participant.user_id,
            first_name: participant.first_name,
            last_name: participant.last_name
          });
        }
      }
      self.messagePositionConversation = messagePosition;
      setTimeout(() => {
        self.scrollable("vs", "100%");
      }, 100);
    },
    selectAddress(item) {
      this.generate_route(item);
    },
    closeAddressModal() {
      this.overlay_ontheway = false;
      this.setSelectedType('text');
    },
    closeAddressRouteModal() {
      this.overlay_map = false;
    },
    setupMessageListener(chatId) {
      const self = this;
      self.teardownMessageListener();
      if (!chatId || !self.tenant_id) return;

      let first_time = true;
      const listenerStartTime = new Date();
      self.messageListenerUnsub = firestore
        .collection("tenants")
        .doc(self.tenant_id)
        .collection("chats")
        .doc(chatId)
        .collection("messages")
        .where("creation_date", ">", listenerStartTime)
        .onSnapshot(snapshot => {
          if (!first_time) {
            for (const change of snapshot.docChanges()) {
              let message = change.doc.data();
              message.message_id = change.doc.id;
              message.id = change.doc.id;
              if (change.type === 'added' || change.type === 'modified') {
                self.addMessage(message);
                if (change.type === 'added') {
                  // Mark incoming messages as read and clear the unread badge
                  // — but only when the tab is ACTUALLY visible. JS in a
                  // backgrounded tab still fires the listener, and without
                  // the visibilityState check we would mark messages read
                  // on behalf of a user who isn't actually looking at the
                  // chat (the classic symptom: sender sees "Read" the
                  // instant they hit send because the recipient's tab is
                  // open in the background).
                  const senderPath = "/tenants/" + self.tenant_id + "/users/" + self.owner_id;
                  const tabVisible = typeof document !== 'undefined' && document.visibilityState === 'visible';
                  const fromOther = message.sender_id !== senderPath;
                  if (fromOther && chatId === self.selectedChatId && tabVisible) {
                    const readUpdate = {};
                    readUpdate[`read_statuses.${self.owner_id}`] = new Date();
                    change.doc.ref.update(readUpdate).catch(() => {});
                    self.$store.dispatch("Chat/update_unread_counter", chatId);
                  }
                  // If the user is scrolled up when a new inbound message
                  // arrives in this chat, surface a badge on the
                  // scroll-to-bottom button so they know there's unread
                  // content below them — and DO NOT force-scroll them
                  // down (prior behavior yanked you back to the bottom
                  // on every new message, losing your scroll position).
                  if (fromOther && chatId === self.selectedChatId && !self.messagesButtomOfPage) {
                    self.unreadBelow = (self.unreadBelow || 0) + 1;
                  } else {
                    self.scrollable("vs", "100%");
                  }
                  if (message.media) {
                    firestore
                      .collection("tenants")
                      .doc(self.tenant_id)
                      .collection("files")
                      .doc(message.media.file_id).get().then(media => {
                        self.$store.commit("Chat/add_chat_media_file", [media]);
                      }).catch(error => {
                        console.error('[Chat] media file fetch failed:', error);
                      });
                  }
                }
              } else if (change.type === 'removed') {
                self.$store.commit("Chat/remove_message", change.doc.id);
              }
            }
          } else {
            first_time = false;
          }
        }, error => {
          console.warn('Messages listener denied:', error.code);
        });
    },
    teardownMessageListener() {
      if (this.messageListenerUnsub) {
        this.messageListenerUnsub();
        this.messageListenerUnsub = null;
      }
    },
    getUserTyping() {
      let user_writing = [];
      if (this.selectedChatId && this.selectedChat && this.selectedChat.user_writing && this.logged) {
        for (const owner_id in this.selectedChat.user_writing) {
          if (
            owner_id !== this.logged.owner_id &&
            this.selectedChat.user_writing[owner_id] > 0 &&
            this.selectedChat.user_writing[owner_id]
            >
            new Date(new Date().toGMTString()).getTime() / 1000) {
            user_writing.push({ owner_id: owner_id, time: this.selectedChat.user_writing[owner_id] });
          }
        }
      }

      if (user_writing.length > 0) {
        user_writing = user_writing.sort((a, b) => a.time < b.time ? 1 : (a.time > b.time ? -1 : 0));
        let userTyping = this.uxDocs[user_writing[0].owner_id];

        this.userTypingText = userTyping ? `${userTyping.first_name} ${userTyping.last_name} is typing ...` : "";
      } else {
        this.userTypingText = "";
      }
    },
    hideDeleteGroupDialog() {
      this.showDeleteGroupDialog = false;
    },
    onReaction(reaction, message) {
      // Optimistic local update — message listener doesn't pick up modifications to old messages
      const msg = this.$store.state.Chat.messages.find(m => m.message_id === message.message_id);
      if (msg) {
        if (!msg.reactions) msg.reactions = {};
        if (!msg.reactions[this.owner_id]) msg.reactions[this.owner_id] = [];
        const idx = msg.reactions[this.owner_id].indexOf(reaction);
        if (idx === -1) {
          msg.reactions[this.owner_id].push(reaction);
        } else {
          msg.reactions[this.owner_id].splice(idx, 1);
        }
        this.$forceUpdate();
      }
      this.$store.dispatch("Chat/reaction", {
        chat_id: message.chat_id,
        message_id: message.message_id,
        reaction: reaction
      });
    },
    //no-unused-vars
    onEventAction(action, message) {
      if (["change"].includes(action)) {
        this.messageEventSelected = message;
        this.showEventAsistanceModal = true;
        this.showCancelOptionsForEvent = false;
      } else if (["cancel"].includes(action)) {
        this.messageEventSelected = message;
        this.showEventAsistanceModal = true;
        this.showCancelOptionsForEvent = true;
      }
    },
    onEventActionSelected(action) {
      const self = this;
      if (["accepted"].includes(action)) {
        self.$store.dispatch("Chat/change_event_rvsp", { status: "accepted", message: self.messageEventSelected })
      } else if (["declined"].includes(action)) {
        self.$store.dispatch("Chat/change_event_rvsp", { status: "declined", message: self.messageEventSelected })
      } else if (["cancel"].includes(action)) {
        self.$store.dispatch("Chat/cancel_event", self.messageEventSelected);
      }

      this.messageEventSelected = null;
      this.showEventAsistanceModal = false;
    },
    bottomVisible() {
      const window2 = document.querySelector(".conversation-list-scroll.__vuescroll > .__panel");
      if (!window2) return false;
      const scrollY = window2.scrollTop;
      const visible = window2.clientHeight;
      const pageHeight = window2.scrollHeight;
      const bottomOfPage = visible + scrollY >= pageHeight;
      return bottomOfPage || (pageHeight - (visible + scrollY)) <= 250;
    },
    addConversations() {
      return new Promise((resolve, reject) => {
        const self = this;
        let conversations_bulk = [];
        this.bottomLoading = true;
        let path = "/tenants/" + self.tenant_id + "/users/" + self.owner_id;
        let conversations = firestore
          .collection("tenants")
          .doc(self.tenant_id)
          .collection("chats")
          .where('participant_ids', 'array-contains', path)
          .orderBy("last_message.date", "desc");

        // if (self.xconversations.length > 0) {
        //   conversations = conversations.startAt(self.xconversations[self.xconversations.length - 1].last_message.date);
        // }

        conversations.get().then(conversations => {
          conversations.forEach(doc => {
            let data = doc.data();
            data = {
              ...data,
              chat_id: doc.id
            };
            conversations_bulk.push(data);
          });
          self.$store.commit('Chat/addConversationBulk', conversations_bulk);
          this.bottomLoading = false;

          setTimeout(() => {
            if (self.bottomVisible() && conversations_bulk.length === 10) {
              if (self.xconversations.length > 0 && self.previousStartAt !== self.xconversations[self.xconversations.length - 1].last_message.date) {
                // self.addConversations();
                self.previousStartAt = self.xconversations[self.xconversations.length - 1].last_message.date;
              }
            }
          }, 100);

          resolve();
        }).catch((error) => {
          console.error('[Chat] addConversations failed:', error);
          this.bottomLoading = false;
          reject(error);
        });
      });
    },
    handleScrollMessages() {
      this.ignoreMessagesScrollEvent = false;
    },
    addMessage(message) {
      this.$store.dispatch("Chat/add_message", message);
      if (message.sender_id && message.sender_id.includes(this.owner_id)) {
        this.lastMessageSentByMe = false;
      }
    },
    async init() {
      const self = this;
      let path = "/tenants/" + self.tenant_id + "/users/" + self.owner_id;

      self.classArray.push('display-text-options');

      if (navigator.serviceWorker && !self._initSwListenerAdded) {
        self._initSwListenerAdded = true;
        self._initSwHandler = (event) => {
          self.selectedConversationFromNotificationData(event.data);
        };
        navigator.serviceWorker.addEventListener('message', self._initSwHandler);
      }

      self.geolocate();
      axios.defaults.headers.common['Authorization'] = localStorage.getItem('auth_id');

      // Real-time message listener for the selected chat (tenant-scoped, not collectionGroup)

      try {
        self.$store.commit("Chat/initError", false);
        await self.$store.dispatch('Chat/logged');
        await self.$store.dispatch('Chat/get_users_global');
        await self.addConversations();
      } catch (error) {
        console.error('[Chat] Init failed:', error);
        self.$store.commit("Chat/initError", true);
      }

      self.selectedConversation({});

      self.$store.commit("Chat/preloading", false);
      setTimeout(async () => {
        self.suscriptions.push(firestore
          .collection('users')
          .where("tenant_ids", "array-contains", self.tenant_id)
          .onSnapshot(async snapshot => {
            for (const change of snapshot.docChanges()) {
              if (['modified'].includes(change.type)) {
                const user = change.doc.data();
                user.id = change.doc.id;
                let userGlobal = this.uxDocs[user.id];
                if (userGlobal) {
                  if (
                    userGlobal.first_name !== user.first_name ||
                    userGlobal.last_name !== user.last_name ||
                    userGlobal.company_name !== user.company_name
                  ) {
                    self.$store.commit("Chat/updateUserGlobal", change.doc);
                  }
                }

                const current_tenant = (user.tenants_list || []).find(x => x.tenant_id == self.tenant_id);
                if (current_tenant && current_tenant.status === "inactive") {
                  self.$store.dispatch("Chat/logout");
                }
              }
            }
          }, error => {
            console.error('[Chat] Global users listener error:', error);
          }));

        self.suscriptions.push(firestore
          .collection('tenants')
          .doc(self.tenant_id)
          .collection('users')
          .onSnapshot(async snapshot => {
            for (const change of snapshot.docChanges()) {
              if (['modified'].includes(change.type)) {
                self.$store.commit("Chat/updateUserInner", change.doc);
              }
            }
          }, error => {
            console.error('[Chat] Tenant users listener error:', error);
          }));

        // suscription new conversations
        let first_time_conversations = true;
        self.suscriptions.push(firestore
          .collection("tenants")
          .doc(self.tenant_id)
          .collection("chats")
          .where('participant_ids', 'array-contains', path)
          // .where("created_at", ">", moment().subtract(1, "days").toDate().getTime())
          .onSnapshot(async snapshot => {
            if (!self._seenLastMessageIds) self._seenLastMessageIds = new Set();
            if (!first_time_conversations) {
              snapshot.docChanges().forEach(change => {
                let data = change.doc.data();
                data.chat_id = change.doc.id;

                if (['added', 'modified'].includes(change.type)) {
                  // Fire a desktop notification if this update represents a new
                  // inbound message we haven't surfaced yet. Runs in both browser
                  // and Electron — in Electron it's the only path (FCM is dead);
                  // in browsers it complements FCM (and is no-op when permission
                  // isn't granted) so it acts as a fallback for tabs that don't
                  // receive a push (e.g., service worker hiccups).
                  self.maybeNotifyForConversation(data);

                  // Sticky badge tracking: if the incoming unread count for me
                  // is higher than what we have locally, bump the sticky count.
                  // We never decrease from here — only a click into the chat
                  // clears it (see selectedConversation / check_selected).
                  if (data.chat_id !== self.selectedChatId && data.unread_counters) {
                    let myKey = self.owner_id;
                    if (data.unread_counters[myKey] === undefined) {
                      const prefix = '/tenants/' + self.tenant_id + '/users/';
                      const otherIds = (data.participants || []).filter(p => p.user_id !== self.owner_id).map(p => p.user_id);
                      const ids = (data.participant_ids || []).map(p => p.replace(prefix, ''));
                      const found = ids.find(id => !otherIds.includes(id));
                      if (found) myKey = found;
                    }
                    const liveCount = data.unread_counters[myKey] || 0;
                    const currentSticky = self.stickyUnread[data.chat_id] || 0;
                    if (liveCount > currentSticky) {
                      self.$set(self.stickyUnread, data.chat_id, liveCount);
                    }
                  }

                  const tabVisibleForConv = typeof document !== 'undefined' && document.visibilityState === 'visible';
                  if (data.chat_id === self.selectedChatId && tabVisibleForConv) {
                    // Chat is actively open AND the tab is visible — treat
                    // any new activity as read. Zero the local counter so
                    // the sidebar badge clears immediately, then sync to
                    // Firestore + notification badge in the background.
                    // If the tab is backgrounded we skip this so we don't
                    // mark messages read on behalf of a user who isn't
                    // actually looking at the chat.
                    data = {
                      ...data,
                      unread_counters: {
                        ...(data.unread_counters || {}),
                        [self.owner_id]: 0
                      },
                      unread_messages_count: 0
                    }
                    self.$store.dispatch("Chat/update_unread_counter", data.chat_id);
                    // Fallback: when conversation updates with a new message but the
                    // message listener missed it, fetch the latest message directly
                    if (change.type === 'modified' && data.last_message) {
                      const lastMsgId = data.last_message.message_id || data.last_message_id;
                      const messages = self.$store.state.Chat.messages;
                      const alreadyHave = lastMsgId && messages.some(m => m.message_id === lastMsgId || m.id === lastMsgId);
                      if (lastMsgId && !alreadyHave) {
                        firestore
                          .collection("tenants").doc(self.tenant_id)
                          .collection("chats").doc(data.chat_id)
                          .collection("messages")
                          .orderBy("creation_date", "desc")
                          .limit(1)
                          .get().then(snap => {
                            if (!snap.empty) {
                              const msg = snap.docs[0].data();
                              msg.message_id = snap.docs[0].id;
                              msg.id = snap.docs[0].id;
                              const stillMissing = !self.$store.state.Chat.messages.some(m => m.message_id === msg.message_id);
                              if (stillMissing) {
                                self.addMessage(msg);
                                self.scrollable("vs", "100%");
                              }
                            }
                          }).catch(err => console.error('[Chat] fallback message fetch:', err));
                      }
                    }
                  }
                  if (['modified'].includes(change.type)) {
                    self.$store.commit('Chat/updateConversation', data);
                  } else {
                    self.$store.commit('Chat/addConversation', data);
                  }
                }

                if (change.type === 'removed') {
                  self.$store.commit('Chat/deleteConversation', change.doc.id);
                  self.selectedConversation({});
                  self.setSelectedType('empty');
                }
              });
            } else {
              first_time_conversations = false;
              // Seed the seen-set with every existing chat's last_message_id so
              // we don't fire desktop notifications for messages that were
              // already in the inbox when the app loaded.
              snapshot.docs.forEach(doc => {
                const d = doc.data();
                const lastMsgId = (d.last_message && (d.last_message.message_id || d.last_message.id)) || d.last_message_id;
                if (lastMsgId) self.rememberSeenId(lastMsgId);
              });
              // Open conversation from notification deep link
              if (self._pendingOpenConversation) {
                self.$nextTick(() => {
                  self.openConversationById(self._pendingOpenConversation);
                  self._pendingOpenConversation = null;
                });
              }
            }
          }, error => {
            console.error('[Chat] Conversations listener error:', error);
          }));

        // Watch logout
        self.suscriptions.push(firestore.collection("devices").doc(self.device_id)
          .onSnapshot(doc => {
            if (!doc.exists) {
              self.logout();
            }
          }, error => {
            console.error('[Chat] Device listener error:', error);
          }));

        // Decide whether to surface the "Enable notifications" banner before
        // touching FCM. Browsers (especially Safari/Chrome) silently reject
        // permission requests that don't originate from a user gesture, so
        // calling messaging.getToken() on mount when permission is 'default'
        // never actually prompts. Show the banner instead and let the user
        // click Enable, which calls Notification.requestPermission() from a
        // real click handler. Skip entirely in Electron — the desktop app
        // uses native OS notifications via IPC and never needs browser perms.
        const isElectron = !!window.electronAPI;
        const notifPermission = (typeof Notification !== 'undefined') ? Notification.permission : 'denied';

        if (!isElectron && notifPermission !== 'granted') {
          // 'default' (never asked) or 'denied' — show the banner so the user
          // can opt in (or be reminded to enable in browser settings).
          self.showEnableNotifications = true;
        }

        // Skip the entire FCM block in Electron — Chromium in Electron does not
        // deliver Web Push, so getToken/onMessage are dead-ends. Electron uses
        // the Firestore-driven notification path in the conversations listener.
        if (messaging && !isElectron && notifPermission === 'granted') {
          messaging.getToken({
            vapidKey: config.push_key
          })
            .then((currentToken) => {
              if (currentToken) {
                self.showEnableNotifications = false;
                self.$store.dispatch('Chat/register_device', { device_token: currentToken });
              }
            })
            .catch((err) => {
              console.warn('[Chat] FCM getToken failed:', err);
              // Only show the banner if the user hasn't granted permission yet.
              // If permission is already granted, this is a transient failure
              // (network, service worker not ready, token rotation) — don't prompt.
              if (!isElectron && notifPermission !== 'granted') {
                self.showEnableNotifications = true;
              }
            });

          // Handle foreground push notifications (when tab is active)
          messaging.onMessage(async (payload) => {
            const data = payload.data || {};
            const notification = payload.notification || {};

            // Don't show notification if the user is already viewing this chat
            // AND the tab is actually in the foreground. If the tab is
            // backgrounded we still want to surface the message.
            const tabVisible = (typeof document !== 'undefined' && document.visibilityState === 'visible');
            if (tabVisible && data.conversation_id && data.conversation_id === self.selectedChatId) {
              return;
            }

            const title = notification.title || 'Kiey';
            const body = notification.body || '';

            if (window.electronAPI) {
              // Electron: use native OS notification via IPC
              window.electronAPI.showNotification(title, body, data);
              return;
            }
            if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;

            // Cross-path dedupe — see maybeNotifyForConversation. If the
            // Firestore conversations listener already surfaced this conversation
            // moments ago, skip so we don't double-notify (and double-sound).
            if (self.notifiedConversationRecently(data.conversation_id)) return;

            // Prefer the service worker registration's showNotification over
            // `new Notification()`. SW notifications are persistent and are
            // reliably rendered by Chrome as banners even when the tab is
            // backgrounded but the browser itself is foregrounded — whereas
            // page-scope `new Notification()` in that state often plays the
            // sound without popping the banner.
            try {
              const reg = navigator.serviceWorker && (await navigator.serviceWorker.ready);
              if (reg) {
                await reg.showNotification(title, {
                  body,
                  icon: '/favicon.ico',
                  tag: data.conversation_id ? 'kiey-msg-' + data.conversation_id : 'kiey-message',
                  requireInteraction: true,
                  data,
                });
                return;
              }
            } catch (e) {
              console.warn('[Chat] SW showNotification failed, falling back:', e);
            }
            // Fallback: page-scope Notification (may not banner in background tabs)
            const n = new Notification(title, {
              body,
              icon: '/favicon.ico',
              tag: data.conversation_id ? 'kiey-msg-' + data.conversation_id : 'kiey-message',
              requireInteraction: true,
            });
            n.onclick = () => {
              window.focus();
              if (data.conversation_id) {
                self.selectedConversationFromNotificationData(data);
              }
              n.close();
            };
          });

          // Listen for FCM token refreshes and re-register with backend
          navigator.serviceWorker && navigator.serviceWorker.ready.then(() => {
            // Periodically check for token changes (FCM doesn't expose a direct refresh callback in compat SDK)
            self._tokenRefreshInterval = setInterval(() => {
              messaging.getToken({ vapidKey: config.push_key })
                .then((refreshedToken) => {
                  if (refreshedToken) {
                    self.$store.dispatch('Chat/register_device', { device_token: refreshedToken });
                  }
                })
                .catch((err) => {
                  console.warn('Token refresh check failed:', err);
                });
            }, 30 * 60 * 1000); // Check every 30 minutes
          });
        }

      }, 1);
    },
    showErrorToast(message) {
      this.errorToast = message;
      if (this.errorToastTimer) clearTimeout(this.errorToastTimer);
      this.errorToastTimer = setTimeout(() => { this.errorToast = ""; }, 5000);
    },
    // Track which conversations' last-message we've already notified for, capped
    // so the Set can't grow unbounded over a long session (insertion-ordered,
    // so we evict the oldest entry once over the cap).
    rememberSeenId(id) {
      if (!id) return;
      if (!this._seenLastMessageIds) this._seenLastMessageIds = new Set();
      this._seenLastMessageIds.add(id);
      if (this._seenLastMessageIds.size > 500) {
        this._seenLastMessageIds.delete(this._seenLastMessageIds.values().next().value);
      }
    },
    // Cross-path notification dedupe. Two browser paths can each surface a
    // notification for the same inbound message: the Firestore conversations
    // listener (maybeNotifyForConversation, keyed on message_id) and the FCM
    // foreground handler (messaging.onMessage). The FCM payload carries only
    // conversation_id — no message_id — so the two can't share the
    // _seenLastMessageIds guard. Both funnel through here instead: the first
    // path to notify for a conversation records the time and proceeds; any
    // other path for the same conversation within the window is suppressed,
    // collapsing the duplicate banner + duplicate sound into one. Returns true
    // when the caller should SKIP because we notified for this conv recently.
    notifiedConversationRecently(convId) {
      if (!convId) return false;
      const nowMs = Date.now();
      if (!this._recentlyNotifiedConv) this._recentlyNotifiedConv = new Map();
      const last = this._recentlyNotifiedConv.get(convId);
      if (last && (nowMs - last) < 5000) return true;
      this._recentlyNotifiedConv.set(convId, nowMs);
      // Bound the map (insertion-ordered → evict the oldest entry over the cap).
      if (this._recentlyNotifiedConv.size > 500) {
        this._recentlyNotifiedConv.delete(this._recentlyNotifiedConv.keys().next().value);
      }
      return false;
    },
    // Build a display name for a conversation doc — prefers explicit title,
    // falls back to the first non-self participant's name from uxDocs.
    _convDisplayName(conv) {
      if (conv.title) return conv.title;
      if (Array.isArray(conv.participants)) {
        for (const p of conv.participants) {
          if (p.user_id === this.owner_id) continue;
          const u = this.uxDocs[p.user_id];
          if (u) {
            const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
            if (name) return name;
          }
          if (p.first_name || p.last_name) {
            return [p.first_name, p.last_name].filter(Boolean).join(' ').trim();
          }
        }
      }
      return 'Kiey';
    },
    // Build the notification body from a conversation's last_message.
    // Backend stores message body on `content` (see send_text.js).
    _convNotificationBody(conv) {
      const lm = conv.last_message;
      if (!lm) return '';
      if (lm.type === 'media') return 'Photo';
      if (lm.type === 'call') return lm.content || 'Incoming call';
      const raw = lm.content || '';
      // Strip HTML and decode common entities (mirrors Conversation/script.js)
      return raw
        .replace(/<[^>]*>/g, '')
        .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"').replace(/&#39;/g, "'")
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 140);
    },
    // Fire a desktop notification for a new inbound message in `conv`.
    // No-ops when: message already seen, sender is me, chat is currently open,
    // or in a browser tab without notification permission.
    async maybeNotifyForConversation(conv) {
      try {
        if (!conv || !conv.last_message) return;
        const lm = conv.last_message;
        const lastMsgId = lm.message_id || lm.id || conv.last_message_id;
        if (!lastMsgId) return;

        // Already surfaced this message — don't double-notify.
        if (this._seenLastMessageIds && this._seenLastMessageIds.has(lastMsgId)) return;
        this.rememberSeenId(lastMsgId);

        // Skip messages I sent myself. Backend stores sender as
        // `{ id: '/tenants/{tid}/users/{ownerId}', ... }`.
        const senderPath = lm.sender && lm.sender.id;
        if (senderPath && this.owner_id && senderPath.endsWith('/' + this.owner_id)) return;

        // Skip if this chat is currently open and the window is focused —
        // the user is already looking at it.
        const windowFocused = (typeof document !== 'undefined') && document.hasFocus && document.hasFocus();
        if (conv.chat_id === this.selectedChatId && windowFocused) return;

        const title = this._convDisplayName(conv);
        const body = this._convNotificationBody(conv);
        const data = { conversation_id: conv.chat_id };

        if (window.electronAPI) {
          // Electron: native OS notification via IPC (also bumps dock badge).
          // This is the ONLY notification path in Electron — FCM doesn't work
          // in Chromium-in-Electron, so messaging.onMessage never fires.
          window.electronAPI.showNotification(title, body, data);
          return;
        }

        // Browser fallback: if FCM delivery hiccups (SW unregistered, token
        // expired, push dropped, etc.) we still want to surface the message.
        // The foreground FCM onMessage handler and the service worker both
        // use a `tag` keyed to conversation_id, and so do we here — so if
        // one of them already fired, this second call replaces the same
        // notification (same tag) rather than stacking a duplicate.
        if (typeof Notification === 'undefined') return;
        if (Notification.permission !== 'granted') return;
        // Cross-path dedupe: the FCM onMessage handler may also fire for this
        // same message — collapse the pair into one banner + one sound.
        if (this.notifiedConversationRecently(conv.chat_id)) return;
        // Prefer the SW registration's showNotification (see messaging.onMessage
        // for rationale — background-tab banners work reliably there).
        try {
          const reg = navigator.serviceWorker && (await navigator.serviceWorker.ready);
          if (reg) {
            await reg.showNotification(title, {
              body,
              icon: '/favicon.ico',
              tag: 'kiey-msg-' + conv.chat_id,
              requireInteraction: true,
              data,
            });
            return;
          }
        } catch (e) {
          console.warn('[Chat] SW fallback notification failed:', e);
        }
        const n = new Notification(title, {
          body,
          icon: '/favicon.ico',
          tag: 'kiey-msg-' + conv.chat_id,
          requireInteraction: true,
        });
        n.onclick = () => {
          window.focus();
          this.selectedConversationFromNotificationData(data);
          n.close();
        };
      } catch (err) {
        console.warn('[Chat] maybeNotifyForConversation failed:', err);
      }
    },
    openUpdatePage() {
      if (window.electronAPI && window.electronAPI.openDownloadPage) {
        window.electronAPI.openDownloadPage();
      } else {
        window.open('https://web.kiey.com/download', '_blank');
      }
    },
    enableNotifications() {
      const self = this;
      if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') {
        Notification.requestPermission().then((permission) => {
          if (permission === 'granted' && messaging) {
            messaging.getToken({ vapidKey: config.push_key })
              .then((currentToken) => {
                if (currentToken) {
                  self.showEnableNotifications = false;
                  self.$store.dispatch('Chat/register_device', { device_token: currentToken });
                }
              })
              .catch((err) => {
                console.warn('Failed to get push notification token:', err);
              });
          }
        });
      }
      self.showEnableNotifications = false;
    },
    findContactSuggestion(search, showSuggestion) {
      // Only surface the @-mention picker for group chats — in 1:1 the
      // dropdown UI is hidden, so Enter must still send the message.
      const isGroup = !!(this.selectedChat && this.selectedChat.is_group);
      if (showSuggestion && isGroup) {
        this.showSuggestion = true;
        this.currentWord = search;
      } else {
        this.showSuggestion = false;
        this.currentWord = "";
      }
    },
    selectSuggestionContact(user) {
      if (!user || !this.$refs.textEditor) return;
      const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
      this.$refs.textEditor.selectSuggestion(name);
      this.showSuggestion = false;
      this.currentWord = "";
    },
    moveSuggestion(direction) {
      const list = this.$refs.suggestionList;
      if (!list) return;
      if (direction === 'down') list.moveDown();
      else if (direction === 'up') list.moveUp();
    },
    selectActiveSuggestion() {
      const list = this.$refs.suggestionList;
      if (list && list.selectActive()) return;
      // No usable suggestion (e.g. user typed @xyz with no matches). Preserve
      // pre-fix behavior: dismiss the picker and send the message.
      this.showSuggestion = false;
      this.currentWord = "";
      this.pressEnter();
    },
    dismissSuggestion() {
      this.showSuggestion = false;
      this.currentWord = "";
    },
    toggleEmojiPicker(e) {
      if (this.showEmojiPicker) {
        this.showEmojiPicker = false;
        return;
      }
      // Anchor the picker to the button's viewport rect using position:fixed so
      // it can't be clipped by the composer/chat overflow or buried behind the
      // input bar's stacking context (the bug on Windows). Right-align to the
      // button, sit just above it, and clamp into the viewport.
      let style = { position: 'fixed', zIndex: 2000 };
      try {
        const btn = (e && (e.currentTarget || e.target)) ? (e.currentTarget || e.target).closest('button') : null;
        const r = btn ? btn.getBoundingClientRect() : null;
        if (r) {
          const width = Math.min(500, window.innerWidth * 0.9);
          let left = r.right - width;
          if (left < 8) left = 8;
          style.left = Math.round(left) + 'px';
          style.bottom = Math.round(window.innerHeight - r.top + 8) + 'px';
        } else {
          style.right = '12px';
          style.bottom = '64px';
        }
      } catch (err) {
        style.right = '12px';
        style.bottom = '64px';
      }
      this.emojiPickerStyle = style;
      this.showEmojiPicker = true;
    },
    addEmoji(emoji) {
      if (!emoji) return;
      const char = emoji.native || emoji.colons || '';
      if (char && this.$refs.textEditor) {
        this.$refs.textEditor.addCustom(char);
      }
      this.showEmojiPicker = false;
    },
    onDocumentClick(e) {
      if (this.showEmojiPicker && !e.target.closest('.emoji-container') && !e.target.closest('.cmp-icon[title="Emoji"]')) {
        this.showEmojiPicker = false;
      }
    },
    logout() {
      const self = this;
      localStorage.removeItem('WA_id');
      localStorage.removeItem('token');
      localStorage.removeItem('customToken');
      localStorage.removeItem('owner_id');
      localStorage.removeItem('auth_id');
      localStorage.removeItem('tenant_id');
      localStorage.removeItem('user_doc');
      localStorage.removeItem('device_id');
      self.unsuscribe();
      self.tokenCleared = true;
      self.$store.dispatch('Chat/logout').then(() => {
        self.$store.dispatch('Chat/web_token');
      });
    },
    getImageURL(media, list) {
      if (media.file_id) {
        let file = list.find(f => f.id === media.file_id);
        if (file) {
          return file['url'];
        }
      } else {
        return media.media_preview_url;
      }
    },
    getMediaPreviewURL(id, list) {
      let file = list.find(f => f.id === id);
      if (file) {
        return file['media_preview_url'];
      }
    },
    showImagePreview(url) {
      const self = this;

      const openWith = (w, h) => {
        try {
          // Replace items[0] entirely (not mutate) to clear any residual
          // fields left over from showVideoPreview (e.g. stale `html` key)
          // which would otherwise make PhotoSwipe try to render HTML instead
          // of the image and throw during init.
          self.$set(self.previewImageData.items, 0, {
            src: url,
            w: w || 1200,
            h: h || 900
          });
          // Force close → open toggle so the v-photoswipe watcher creates
          // a fresh PhotoSwipe instance even if a prior one is lingering.
          self.previewImageData.isOpen = false;
          self.$set(self.previewImageData.options, 'index', 0);
          self.$nextTick(() => {
            self.previewImageData.isOpen = true;
            self.$nextTick(() => {
              const close = document.getElementsByClassName('pswp__button--close')[0];
              if (close) close.setAttribute('title', '');
              const zoom = document.getElementsByClassName('pswp__button--zoom')[0];
              if (zoom) zoom.setAttribute('title', '');
            });
          });
        } catch (e) {
          console.error('[Chat] showImagePreview failed:', e);
          self.previewImageData.isOpen = false;
        }
      };

      const probe = (src, attempt) => {
        const img = new Image();
        img.onload = function () {
          const w = this.naturalWidth || this.width;
          const h = this.naturalHeight || this.height;
          openWith(w, h);
        };
        img.onerror = () => {
          if (attempt < 1) {
            // Transient failure (cache/network/CORS race) — retry once with a cache-buster
            const sep = src.indexOf('?') === -1 ? '?' : '&';
            probe(src + sep + '_retry=' + Date.now(), attempt + 1);
          } else {
            // Still failing — open PhotoSwipe with fallback dims so the user sees
            // the image (or a broken-image placeholder) instead of a silent no-op.
            openWith(0, 0);
          }
        };
        img.src = src;
      };

      probe(url, 0);
    },
    showVideoPreview(url, previewUrl) {
      const self = this;
      const safeUrl = String(url || '');
      const safePoster = String(previewUrl || '');
      const videoId = `kiey-preview-video-${Date.now()}`;

      // Build the preview with the rotation baked into the MP4's tkhd atom.
      // iOS clips carry a display matrix that some Chromium builds silently
      // ignore — without the transform those play upside down or sideways.
      //
      // To avoid double-rotating clips that Chromium DID honor, after the
      // video loads we compare its videoWidth/videoHeight against the tkhd
      // display dimensions. If they match, Chromium already applied the
      // matrix and we strip our transform.
      const openModal = (rotationInfo) => {
        const { rotation, displayWidth, displayHeight } = rotationInfo;

        // Wrap the video in a flex box that fills the PhotoSwipe slot. For
        // 90°/270° we size the video in viewport units swapped so the rotated
        // frame fits the screen.
        let videoStyle;
        if (rotation === 90 || rotation === 270) {
          videoStyle = 'height: 100vw; width: 100vh; max-width: 100vh; max-height: 100vw; object-fit: contain;';
        } else {
          videoStyle = 'width: 100%; height: 100%; max-width: 100vw; max-height: 100vh; object-fit: contain;';
        }
        const transformStyle = rotation ? `transform: rotate(${rotation}deg);` : '';

        const videoHtml =
          `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;padding-top:40px;overflow:hidden;">` +
            `<video id="${videoId}" poster="${safePoster}" controls autoplay playsinline ` +
              `data-kiey-rotation="${rotation}" ` +
              `data-kiey-display-w="${displayWidth}" ` +
              `data-kiey-display-h="${displayHeight}" ` +
              `style="${videoStyle}${transformStyle}visibility:hidden;">` +
              `<source src="${safeUrl}" type="video/mp4">` +
            `</video>` +
          `</div>`;

        self.previewImageData.items[0] = { html: videoHtml };
        self.previewImageData.isOpen = true;
        self.$set(self.previewImageData.options, 'index', 0);

        if (document.getElementsByClassName('pswp__button--close').length > 0) {
          document.getElementsByClassName('pswp__button--close')[0].setAttribute('title', '');
        }
        if (document.getElementsByClassName('pswp__button--zoom').length > 0) {
          document.getElementsByClassName('pswp__button--zoom')[0].setAttribute('title', '');
        }

        // After PhotoSwipe renders the html, attach a loadedmetadata listener
        // to check whether Chromium honored the rotation. If it did, remove
        // our CSS transform so we don't double-rotate. Either way, only
        // un-hide the video once we've made the decision so the user never
        // sees a mid-correction flash.
        const attachWhenReady = (attempt = 0) => {
          const video = document.getElementById(videoId);
          if (!video) {
            if (attempt < 20) setTimeout(() => attachWhenReady(attempt + 1), 50);
            return;
          }
          const reveal = () => {
            // We pre-applied a CSS rotate() matching the MP4's rotation matrix.
            // But mainstream browsers honor that matrix natively, so on a normal
            // browser our transform double-rotates the clip — it plays sideways
            // for 90/270, upside-down for 180. Only Electron's bundled Chromium
            // historically ignores the matrix on H.264, so there we keep our
            // transform.
            //
            // (The previous approach compared videoWidth/Height to the parsed
            // tkhd display dims to detect "did the browser honor it". But iOS
            // stores those tkhd dims PRE-rotation, so for a 90/270 clip they
            // never matched the browser's post-rotation size → false negative →
            // the double-rotation that made clips appear sideways.)
            if (rotation !== 0) {
              const isElectron = /electron/i.test(navigator.userAgent || '');
              if (!isElectron) {
                // Browser already applied the rotation — drop our transform and
                // restore natural sizing so we don't rotate on top of it.
                video.style.transform = '';
                if (rotation === 90 || rotation === 270) {
                  video.style.width = '100%';
                  video.style.height = '100%';
                  video.style.maxWidth = '100vw';
                  video.style.maxHeight = '100vh';
                }
              }
            }
            video.style.visibility = 'visible';
          };
          if (video.readyState >= 1) {
            reveal();
          } else {
            video.addEventListener('loadedmetadata', reveal, { once: true });
            // Safety: if metadata never fires (rare), reveal anyway after 1.5s.
            setTimeout(() => { if (video.style.visibility === 'hidden') reveal(); }, 1500);
          }
        };
        self.$nextTick(() => attachWhenReady(0));
      };

      // Resolve rotation asynchronously; default to zeros so we never block
      // the preview from opening if the parse fails or takes too long.
      getMp4Rotation(safeUrl)
        .then(openModal)
        .catch((err) => {
          console.warn('[Chat] mp4 rotation lookup failed:', err);
          openModal({ rotation: 0, displayWidth: 0, displayHeight: 0 });
        });
    },
    hideImagePreview() {
      let pwVideos = document.querySelectorAll(".pswp__zoom-wrap video");
      for (const video of pwVideos) {
        video.remove();
      }
      this.previewImageData.isOpen = false;
    },
    unsuscribe() {
      for (const suscription of this.suscriptions) {
        suscription ? suscription() : '';
      }
      this.suscriptions = [];
    },
    pressEnter() {
      if (this.valid) {
        this.send_message();
      }
    },
    async changeIconGroup() {
      const self = this;
      try {
        const url = await uploadFileToStorage(self.imageData, 'group', self.tenant_id);
        self.$store.dispatch("Chat/edit_group", {
          icon_url: url,
          chat_id: self.selectedChat.chat_id
        });
      } catch (error) {
        console.error(error);
      }
    },
    async changeTitleGroup() {
      const self = this;
      self.$store.dispatch("Chat/edit_group", {
        title: self.conversationTitle,
        chat_id: self.selectedChat.chat_id
      });
      self.drawerGroupTitle = false;
    },
    isImage(file) {
      const fileTmp = {
        url: file.url
      };
      return isImage(fileTmp);
    },
    isVideo(file) {
      const fileTmp = {
        url: file.url
      };
      return isVideo(fileTmp);
    },
    checkResource(media, list) {
      if (media.media_preview_url) {
        const fileTmp = {
          url: media.file ? media.file.name : (media.file_url ? media.file_url : media.url)
        };
        if (isVideo(fileTmp)) {
          return "video";
        }

        if (isImage(fileTmp)) {
          return "image";
        }

        return "document";

      } else if (media.file_id) {

        let file = list.find(f => f.id === media.file_id);
        if (file) {

          if (isVideo(file)) {
            return "video";
          }

          if (isImage(file)) {
            return "image";
          }

          return "document";
        }
      }
      return false;
    },
    set_icon_url() {
      document.getElementById('dialog_icon_url').click();
    },
    set_file() {
      document.getElementById('file_upload').click();
    },
    set_image() {
      document.getElementById('image_upload').click();
    },
    triggerFileUpload(id) {
      const el = document.getElementById(id);
      if (el) el.click();
    },
    // Pasted image from the composer → run it through the normal upload flow.
    onPasteFile(file) {
      if (file) this.previewFile({ target: { files: [file] } });
    },
    async previewFile(event) {
      const self = this;
      self.typeSelected = "media";
      for (const file of event.target.files) {
        const blob = await self.loadFileResource(file);
        const fileTmp = {
          url: blob
        };

        self.messageContent = {
          chat_id: self.selectedChatId,
          text: "",
          type: "media",
          file: file,
          file_type: isVideo(fileTmp) || isImage(fileTmp) ? "doc" : "doc",
          file_url: "",
          media_preview_url: isVideo(fileTmp) ? await loadImageFromVideo(blob) : blob,
        };

        self.dataToSend(self.messageContent);
        self.messageContent = {};
      }
      self.typeSelected = "text";
      setTimeout(() => {
        self.scrollable("vs", "100%");
      }, 500);
    },
    loadFileResource(file) {
      return new Promise((resolve, reject) => {
        let reader = new FileReader();
        reader.addEventListener("loadend", (ev) => {
          const currentReader = ev.target;
          resolve(currentReader.result);
        });
        reader.addEventListener("error", error => {
          reject(error);
        });
        reader.readAsDataURL(file);
      });
    },
    previewImage(event) {
      const self = this;
      self.url = URL.createObjectURL(event.target.files[0]);
      self.overlay = true;
      self.uploadValue = 0;
      self.picture = null;
      self.imageData = event.target.files[0];

      self.$store.commit('Chat/status_msg', 'sending');
    },
    previewIconUrl(event) {
      const self = this;
      self.url = URL.createObjectURL(event.target.files[0]);
      self.overlay = true;
      self.uploadIconURL = true;
      self.uploadValue = 0;
      self.picture = null;
      self.imageData = event.target.files[0];

      this.changeIconGroup();
    },
    ...mapActions({
      get_messages: "Chat/messages",
      get_users: "Chat/users",
    }),
    handleResize() {
      // Re-evaluate scroll-to-bottom button visibility after container or content
      // resize (e.g. images finish loading, bubble heights recalculated). Skip
      // while the initial-load guard is active so we don't flash the chevron
      // before messages have finished positioning.
      if (this.ignoreMessagesScrollEvent) return;
      if (!this.selectedChat || !this.xmessages[0]) return;
      const messagesWindow = document.querySelector(".messages-scroll.__vuescroll > .__panel");
      if (!messagesWindow) return;
      const delta = messagesWindow.scrollHeight - (messagesWindow.clientHeight + messagesWindow.scrollTop);
      this.messagesButtomOfPage = delta <= 24;
      if (this.messagesButtomOfPage && this.unreadBelow > 0) this.unreadBelow = 0;
    },
    handleScroll(vertical) {
      const self = this;
      if (self.selectedChat && self.xmessages[0] && !self.ignoreMessagesScrollEvent) {
        const messagesWindow = document.querySelector(".messages-scroll.__vuescroll > .__panel");
        if (!messagesWindow) return;
        const scrollY = messagesWindow.scrollTop;
        const visible = messagesWindow.clientHeight;
        const pageHeight = messagesWindow.scrollHeight;
        // 24px tolerance — group chats with variable-height bubbles and async image
        // loads routinely leave scrollTop a few px short of max even when the user
        // has landed at the bottom. Without slack the chevron stays stuck on.
        const atBottom = (pageHeight - (visible + scrollY)) <= 24;
        self.messagesButtomOfPage = atBottom;
        if (atBottom && self.unreadBelow > 0) self.unreadBelow = 0;
        if (vertical.scrollTop < 20) {
          if (self.last_message !== self.xmessages[0].message_id) {
            self.last_message = self.xmessages[0].message_id;
            self.$store.dispatch("Chat/older_messages", {
              top_date: self.xmessages[0].creation_date,
              chat_id: self.xmessages[0].chat_id
            }).then(response => {
              if (response !== []) {
                if (response.length > 2) {
                  try {
                    self.$refs["vs"].scrollIntoView("#" + self.xmessages[response.length].message_id, 0);
                  } catch (e) {
                    self.$refs["vs"].scrollIntoView("#" + self.xmessages[response.length - 1].message_id, 0);
                  }
                }
              }
            });
          }
        }
      }
    },
    add_xparticipants(item) {
      if (!this.selected.includes(item)) {
        this.selected.push(item);
      } else {
        this.selected.splice(this.selected.findIndex(a => a === item), 1);
      }
    },
    isLastOwnMessage(item, index) {
      if (!item || item.owner_id !== this.owner_id) return false;
      const messages = this.xmessagesFiltered || this.xmessages;
      for (let i = index + 1; i < messages.length; i++) {
        if (messages[i].owner_id === this.owner_id) return false;
      }
      return true;
    },
    showDate(index) {
      if (index === 0) {
        return true
      } else {
        let prev = moment(new Date(this.xmessages[index - 1].creation_date)).format('ddd, MMM DD');
        let actual = moment(new Date(this.xmessages[index].creation_date)).format('ddd, MMM DD');
        return ((actual === prev) ? false : true);
      }
    },
    showCategory(index) {
      if (index === 0) {
        return true
      } else {
        let prev = this.xparticipants[index - 1].category;
        let actual = this.xparticipants[index].category;
        return actual !== prev;
      }
    },
    showResultCategory(index) {
      if (index === 0) {
        return true
      } else {
        let prev = this.search_results[index - 1].category;
        let actual = this.search_results[index].category;
        return actual !== prev;
      }
    },
    selectedConversationFromNotificationData(data) {
      const conversation = this.xconversations.find(x => x.chat_id === data.conversation_id);
      this.selectedConversation(conversation);
    },
    openConversationById(chatId) {
      const conversation = this.xconversations.find(x => x.chat_id === chatId);
      if (conversation) {
        this.selectedConversation(conversation);
      }
    },
    selectedConversation(item, callback) {
      const self = this;
      if (!item || Object.keys(item).length === 0) {
        this.selectedChatId = "";
        this.displayOptionsText = false;
        return;
      }
      self.reloadMessagesAvatar = true;
      item.selected = true;

      this.displayOptionsText = false;
      this.cancelReply();
      if (this.$refs.conversationHeader) {
        this.$refs.conversationHeader.resetFilters();
      }

      if (self.$refs.uploadGallery) {
        self.$refs.uploadGallery.hideModal();
      }

      if (item.chat_id && !item.message_id) {
        const conversation = this.conversations.find(x => x.chat_id === item.chat_id);
        if (conversation) self.check_selected(conversation); else return;
        self.check_messages(item, () => {
          callback ? callback() : "";

          if (item && item.chat_id) {
            self.$store.dispatch("Chat/update_unread_counter", item.chat_id);
          } else if (item.owner_id) {
            self.new_chat_mode = 'single';
            self.selected.push(item);
            self.end();
          }
        });
      } else if (item.message_id && item.creation_date) {
        const conversation = this.conversations.find(x => x.chat_id === item.chat_id);
        if (conversation) self.check_selected(conversation); else return;
        self.setSelectedType("text");
        self.scrollingToMessage = true;
        self.$store.dispatch("Chat/older_messages", {
          top_date: new Date(),
          chat_id: item.chat_id,
          limit: false,
        }).then(function () {
          try {
            self.scrollingToMessage = false;
            setTimeout(() => {
              // eslint-disable-next-line no-undef
              jQuery("#scroll > div").animate({
                // eslint-disable-next-line no-undef
                scrollTop: jQuery("#" + item.message_id).position().top
              }, 800, 'swing');
            }, 1000);
          } catch (e) {
            console.error(e);
          }
        });
      } else if (item.owner_id) {
        self.new_chat_mode = 'single';
        self.selected.push(item);
        self.end();
      }

    },
    check_messages(item, callback) {
      const self = this;
      if (item.chat_id) {
        self.messagesButtomOfPage = true;
        self.ignoreMessagesScrollEvent = true;
        self.get_messages({
          chat_id: item.chat_id,
          phone: self.owner_id,
          empty: true,
          chat: item
        }).then(() => {
          callback ? callback() : "";

          // Freeze the "New messages" divider anchor once, after messages
          // are loaded. Uses the snapshotted unreadAnchorCount from the
          // check_selected call. If no unread at open, no divider.
          if (self.unreadAnchorCount > 0 && !self.unreadAnchorMessageId) {
            const msgs = self.$store.state.Chat.messages;
            const idx = Math.max(0, msgs.length - self.unreadAnchorCount);
            const anchorMsg = msgs[idx];
            if (anchorMsg) {
              self.unreadAnchorMessageId = anchorMsg.message_id || anchorMsg.id;
            }
          }

          self.$nextTick(() => {
            // If the chat has a "New messages" anchor, scroll to it
            // (Slack/WhatsApp behavior) so the user starts reading from
            // where they left off. Otherwise scroll to the bottom.
            const anchorId = self.unreadAnchorMessageId;
            const anchorEl = anchorId ? document.getElementById(anchorId) : null;
            if (anchorEl) {
              anchorEl.scrollIntoView({ block: 'center', behavior: 'auto' });
            } else {
              self.scrollable("vs", "100%");
            }
            // Auto-focus the composer input on chat open.
            if (self.$refs.textEditor && typeof self.$refs.textEditor.focus === 'function') {
              self.$refs.textEditor.focus();
            }
          });

          const messagesWindow = document.querySelector(".messages-scroll.__vuescroll > .__panel");
          if (messagesWindow) {
            messagesWindow.removeEventListener('mousewheel', self.handleScrollMessages);
            messagesWindow.removeEventListener('onmousewheel', self.handleScrollMessages);
            messagesWindow.removeEventListener('DOMMouseScroll', self.handleScrollMessages);
            messagesWindow.addEventListener('mousewheel', self.handleScrollMessages);
            messagesWindow.addEventListener('onmousewheel', self.handleScrollMessages);
            messagesWindow.addEventListener('DOMMouseScroll', self.handleScrollMessages);
          }

          self.calculateUnreadCountersPosition();
        });
      } else {
        self.get_messages({}).then(() => {
          self.$nextTick(() => {
            self.scrollable("vs", "100%");
          });
        });
      }

    },
    check_selected(item) {
      const self = this;
      // Preserve the unsent draft of the chat we're leaving so it isn't lost
      // when switching conversations.
      if (self.selectedChatId && self.typeSelected === 'text' && self.$refs.textEditor) {
        try {
          const draft = self.$refs.textEditor.getTextWithSuggestionsBolded();
          if (draft && draft.trim()) self.$set(self.drafts, self.selectedChatId, draft);
          else self.$delete(self.drafts, self.selectedChatId);
        } catch (e) { /* ignore */ }
      }
      if (item && item.chat_id) {
        self.sizeParticipants = (item.participant_ids || []).length;

        self.selectedChatId = item.chat_id;
        // Clear the sticky badge for this chat — user is now actively
        // viewing it in this tab, so any accumulated local unread is read.
        self.$set(self.stickyUnread, item.chat_id, 0);

        // Snapshot unread count for the "New messages" divider before
        // auto-mark-read wipes unread_counters. Re-opening the same chat
        // will snapshot 0 (already read) and the divider won't render.
        let myKey = self.owner_id;
        if (item.unread_counters && item.unread_counters[myKey] === undefined) {
          const prefix = '/tenants/' + self.tenant_id + '/users/';
          const otherIds = (item.participants || []).filter(p => p.user_id !== self.owner_id).map(p => p.user_id);
          const ids = (item.participant_ids || []).map(p => p.replace(prefix, ''));
          const found = ids.find(id => !otherIds.includes(id));
          if (found) myKey = found;
        }
        self.unreadAnchorCount = (item.unread_counters && item.unread_counters[myKey]) || 0;
        self.unreadAnchorMessageId = null;
        self.unreadBelow = 0;

        self.setSelectedType('check_selected');
        self.classArray.push(self.selectedChat.status);
        self.last_message = null;
        // Restore this chat's saved draft (or clear the composer if none).
        self.messageContent = self.drafts[item.chat_id] ? { text: self.drafts[item.chat_id] } : null;
      } else {
        self.selectedChatId = "";
        self.unreadAnchorCount = 0;
        self.unreadAnchorMessageId = null;
      }
    },
    setter(param) {
      const self = this;
      self.valid = param.isValid;
      if (self.valid) {
        if (param.type !== "text") {
          self.messageContent = param.message;
        } else {
          let user_writing = { user_writing: {} };

          if (!self.intervalUserWriting) {
            self.intervalUserWriting = setTimeout(() => {
              self.intervalUserWriting = null;

              user_writing.user_writing[self.logged.owner_id] = 0;

              firestore
                .collection("tenants")
                .doc(self.tenant_id)
                .collection("chats")
                .doc(self.selectedChatId)
                .set(user_writing, { merge: true });
            }, 5000);

            user_writing.user_writing[self.logged.owner_id] = new Date(new Date().toUTCString()).getTime() / 1000 + 5;

            firestore
              .collection("tenants")
              .doc(self.tenant_id)
              .collection("chats")
              .doc(self.selectedChatId)
              .set(user_writing, { merge: true });
          }
        }
      }
    },
    async onUpload() {
      const self = this;
      self.picture = null;
      self.$store.commit("Chat/status_file", 'loading');
      try {
        const url = await uploadFileToStorage(self.imageData, 'messages', self.tenant_id);
        self.picture = url;
        self.messageContent = {
          chat_id: self.selectedChat.chat_id,
          text: ''
        };
        self.messageContent.file_url = url;
        self.messageContent.type = "media";

        const tmpFile = { url };

        if (this.isImage(tmpFile)) {
          self.messageContent.file_type = "doc";
          self.messageContent.file_subtype = "photo";
        } else if (this.isVideo(tmpFile)) {
          self.messageContent.file_type = "doc";
          self.messageContent.file_subtype = "video";
        } else {
          self.messageContent.file_type = "doc";
          self.messageContent.file_subtype = "doc";
        }
        self.dataToSend(self.messageContent);
        self.messageContent = {};
      } catch (error) {
        console.error(error.message);
      }
    },
    onSendFiles(files) {
      const self = this;
      self.typeSelected = "media";

      for (const file of files) {
        self.messageContent = {
          chat_id: self.selectedChat.chat_id,
          text: file.text,
          type: file.type,
          file: file.file,
          file_type: file.file_type,
          file_url: "",
          media_preview_url: file.url
        };

        self.dataToSend(self.messageContent);
        self.messageContent = {};
      }
      self.$refs.uploadGallery.hideModal();
      self.typeSelected = "text";
      self.$nextTick(() => {
        self.scrollable("vs", "100%");
      });
    },
    // Tidy pasted content (e.g. changelog tables from Claude) before sending.
    // Markdown TABLE blocks are preserved verbatim — the web Message renderer
    // turns them into real <table>s, so the | pipe | structure must survive.
    // Only stray markdown horizontal rules (---, ***, ___) that aren't part of
    // a table are stripped (they render as ugly full-width lines otherwise).
    cleanOutgoingText(text) {
      if (!text) return text;
      const lines = text.split('\n');

      // A table block = a row with pipes immediately followed by a separator
      // row (|---|---|). Mark every line that belongs to one so we keep it.
      const hasPipe = (l) => /\|/.test(l);
      const isSeparatorRow = (l) => {
        const t = l.trim();
        return !!t && /\|/.test(t) && /^\|?[\s:|-]+\|?$/.test(t) && /-/.test(t) && !/[a-z0-9]/i.test(t);
      };
      const inTable = new Array(lines.length).fill(false);
      for (let i = 0; i < lines.length - 1; i++) {
        if (hasPipe(lines[i]) && isSeparatorRow(lines[i + 1])) {
          inTable[i] = inTable[i + 1] = true;
          let j = i + 2;
          while (j < lines.length && lines[j].trim() && hasPipe(lines[j])) {
            inTable[j] = true;
            j++;
          }
          i = j - 1;
        }
      }

      const kept = [];
      for (let i = 0; i < lines.length; i++) {
        const raw = lines[i];
        if (inTable[i]) { kept.push(raw); continue; } // preserve table markdown
        const t = raw.trim();
        // markdown horizontal rule: --- *** ___ (3+ of the same char)
        if (t && /^([-*_])\1{2,}$/.test(t.replace(/\s+/g, ''))) continue;
        kept.push(raw);
      }
      return kept.join('\n').replace(/\n{3,}/g, '\n\n').trim();
    },
    dataToSend(message) {
      const self = this;
      self.reloadMessagesAvatar = false;
      if (self.typeSelected === "text") {
        if (!self.$refs.textEditor) return;
        message.text = self.cleanOutgoingText(self.$refs.textEditor.getTextWithSuggestionsBolded());
        if (!message.text) {
          return;
        }
      }
      // Sent → drop any saved draft for this chat.
      if (message.chat_id) self.$delete(self.drafts, message.chat_id);

      // Sending a reply clears the "New messages" divider — you've clearly
      // engaged with the unread messages, so dismiss the marker.
      self.unreadAnchorMessageId = null;
      self.unreadAnchorCount = 0;

      self.messageContent = null;

      if (self.messageToReply) {
        message.message_replied = self.messageToReply.message_id;
      }

      if (self.messageToEdit) {
        message.message_id = self.messageToEdit.message_id;
        message.message_identifier = self.messageToEdit.message_identifier;
        message.creation_date = new Date(self.messageToEdit.creation_date);
      }

      self.lastMessageSentByMe = true;
      self.$store.dispatch("Chat/send_message", message).then(() => {
        self.message_send = false;
        self.$nextTick(() => {
          self.scrollable("vs", "100%");
          // Refocus the composer so the user can immediately type a
          // follow-up without clicking the input again.
          if (self.$refs.textEditor && typeof self.$refs.textEditor.focus === 'function') {
            self.$refs.textEditor.focus();
          }
        });
        self.messageToReply = null;
        self.messageToEdit = null;
      }).catch(() => {
        self.showErrorToast("Message failed to send. Please try again.");
      });

      let user_writing = { user_writing: {} };

      if (!self.logged || !self.logged.owner_id) return;
      user_writing.user_writing[self.logged.owner_id] = 0;

      firestore
        .collection("tenants")
        .doc(self.tenant_id)
        .collection("chats")
        .doc(self.selectedChatId)
        .set(user_writing, { merge: true });
    },
    resendMessage(message) {
      const self = this;
      self.$store.dispatch("Chat/send_message", message).then(() => {
        self.message_send = false;
        self.scrollable("vs", "100%");
      });
    },
    deleteMessage(message) {
      store.dispatch("Chat/delete_message", message);
    },
    forwardMessage(message) {
      this.selectedMessage = message;
      this.showSelectChatModal = true;
      // store.dispatch("Chat/forward_message", message);
    },
    onSelectChatForward(message) {
      this.showSelectChatModal = false;
      if (message.type === "contact") {
        this.new_chat_mode = "single";
        this.selected = [];
        this.selected.push({
          user_id: message.owner_id,
          phone_number: message.phone_number,
          profile_picture: message.profile_picture,
          first_name: message.first_name,
          last_name: message.last_name,
          user_role: message.user_role
        });
        this.end(() => {
          this.processForwardMessage();
        });
      } else if (message.type === "group") {
        this.new_chat_mode = "group";
        this.selectedConversation(this.xconversations.find(x => x.chat_id === message.chat_id));
        this.processForwardMessage();
      }
    },
    processForwardMessage() {

      let message = {
        ...this.selectedMessage
      };

      switch (message.type) {
        case "contact": {
          message.contact.owner_id = message.contact.user_id;
          break;
        }
        case "media": {
          if (!message.media) break;
          const mediaFile = this.mediaFilesChat.find(x => x.id === message.media.file_id);
          if (mediaFile) {
            message.media.file_url = mediaFile.url;
          }
          break;
        }
      }

      this.$store.dispatch("Chat/send_message", {
        ...message,
        message_id: "",
        message_identifier: uuid(),
        chat_id: this.selectedChatId,
        fowarded: true,
        forwarded: true
      });

      this.selectedMessage = null;
    },
    hideSelectChatModal() {
      this.showSelectChatModal = false;
    },
    hideEventAsistanceModal() {
      this.showEventAsistanceModal = false;
    },
    editMessage(message) {
      this.setSelectedType(message.type);
      this.messageToEdit = message;
      this.messageContent = message;
    },
    replyMessage(message) {
      this.messageToReply = message;
      this.setSelectedType("text");
    },
    cancelReply() {
      this.messageToReply = null;
    },
    cancelEdit() {
      this.messageToEdit = null;
    },
    send_message() {
      const self = this;
      self.message_send = true;
      if (self.valid) {
        let message = [];
        switch (self.typeSelected) {
          case 'text':
            if (!self.$refs.textEditor) return;
            self.messageContent = self.$refs.textEditor.getter().message;
            self.message_text = self.messageContent;
            message = self.messageContent;

            if (self.messageContent.type === "text") {
              const urls = self.urlify(self.messageContent.text);
              if (urls.length > 0) {
                message.type = "link";
                message.url = urls[0];
                message.description = message.text;
              }
            }

            break;
          case 'contact':
            message.contacts = [];
            for (var i = 0; i < self.selected.length; i++) {
              message.contacts[i] = {
                chat_id: self.selectedChat.chat_id,
                user_id: self.selected[i].owner_id
              };
              message.type = 'contact';
            }
            break;
          default:
            message = self.messageContent;
            break;
        }


        self.dataToSend(message);
        self.setSelectedType('check_selected');
      }
    },
    next() {
      const self = this;
      if (self.addParticipantMode) {
        self.addParticipantsToGroup();
        return;
      }
      if (self.new_chat_mode === 'group') {
        self.drawerGroup = true;
        self.drawerParticipants = false;
      } else {
        self.end();
      }
    },
    addParticipantsToGroup() {
      const self = this;
      if (!self.selected.length || !self.selectedChat) return;

      const existingParticipants = (self.selectedChat.participants || [])
        .filter(p => p.user_id !== self.owner_id)
        .map(p => ({
          first_name: p.first_name || '',
          last_name: p.last_name || '',
          phone_number: p.phone_number || '',
          user_id: p.user_id
        }));

      const newParticipants = self.selected.map(p => ({
        first_name: p.first_name || '',
        last_name: p.last_name || '',
        phone_number: p.phone_number || '',
        user_id: p.owner_id || p.user_id
      }));

      const allParticipants = [...existingParticipants, ...newParticipants];

      self.drawerParticipants = false;
      self.selected = [];
      self.search = '';
      self.addParticipantMode = false;

      self.$store.dispatch('Chat/new_chat', {
        title: self.selectedChat.title,
        participants: allParticipants,
        conversation_id: self.selectedChat.chat_id,
        icon_url: self.selectedChat.icon_url || null
      }).then(() => {
        // Firestore listener auto-updates participant list
      }).catch(err => {
        console.error('[GROUP] addParticipantsToGroup failed:', err);
      });
    },
    async end(callback) {
      const self = this;
      let valid = true;

      if (callback === undefined && self.new_chat_mode === 'group' && this.$refs.validatorGroupChat) {
        valid = await this.$refs.validatorGroupChat.validate();
      }

      if (valid) {
        self.search = '';
        let toCreate = [...self.selected];
        self.selected = [];
        self.drawerParticipants = false;
        self.drawerGroup = false;

        let participant_ids = [];
        for (const prop in toCreate) {
          if (toCreate[prop].owner_id) {
            toCreate[prop].user_id = toCreate[prop].owner_id;
          }
          participant_ids.push(toCreate[prop].user_id);
        }

        self.$store.dispatch("Chat/get_chat", {
          participant_ids,
          chat_mode: self.new_chat_mode
        }).then(response => {
          const conversation = self.xconversations.find(x => x.chat_id === response);
          if (response && (conversation && !conversation.is_group)) {
            self.selectedConversation(conversation, callback);
          } else {
            let chatParams = {
              title: self.conversationTitle,
              participants: toCreate
            };

            if (self.new_chat_mode === 'group') {
              chatParams = {
                ...chatParams,
                icon_url: null,
              };
            }

            self.$store.dispatch("Chat/new_chat", chatParams).then(response => {
              self.selectedConversation(response, callback);
              self.scrollable('vsParticipants', '0%');
              self.conversationTitle = "";
            }).catch(err => {
              console.error('[GROUP] new_chat failed:', err);
            });
          }
        }).catch(err => {
          console.error('[GROUP] get_chat failed:', err);
        });
      }
    },
    onClickSelectedParticipantChatMenu(item) {
      const self = this;
      if (item.owner_id !== self.owner_id) {
        self.selected.push(item);
        self.end();
      }
    },
    onClickSelectedChatMenu(item) {
      if (item.owner_id === this.owner_id) {
        return;
      }
      const self = this;
      switch (item.title) {
        case 'Add Participant':
          self.addParticipantMode = true;
          self.removeParticipantMode = false;
          self.new_chat_mode = 'group';
          self.selected = [];
          self.search = '';
          self.get_users().then(() => { self.drawerParticipants = true; });
          break;
        case 'Remove Participant':
          self.removeParticipantMode = true;
          self.addParticipantMode = false;
          self.new_chat_mode = 'single';
          self.selected = [];
          self.search = '';
          // Build participant list directly from selectedChat.participants
          self._removeParticipantsList = (self.selectedChat.participants || [])
            .filter(p => p.user_id !== self.owner_id)
            .map(p => ({
              ...p,
              owner_id: p.user_id,
              first_name: p.first_name || '',
              last_name: p.last_name || '',
              phone_number: p.phone_number || '',
              text: ((p.first_name || '') + ' ' + (p.last_name || '')).toLowerCase(),
              category: 'participants',
              categoryOrder: 1,
            }));
          self.$store.commit('Chat/setUsers', self._removeParticipantsList);
          self.drawerParticipants = true;
          break;
        case 'Leave Group':
          self.$store.dispatch('Chat/leave_group', self.selectedChat);
          self.selectedConversation({});
          self.setSelectedType('empty');
          break;
        case 'Mute Group':
          self.$store.dispatch('Chat/mute_group', { chat_id: self.selectedChat.chat_id, isMute: true });
          break;
        case 'Unmute Group':
          self.$store.dispatch('Chat/mute_group', { chat_id: self.selectedChat.chat_id, isMute: false });
          break;
        case 'Re-name Group':
          self.drawerGroupTitle = true;
          break;
        case 'Change Group Photo':
          self.set_icon_url();
          break;
        case 'Mute Chat':
          self.$store.dispatch('Chat/mute_group', { chat_id: self.selectedChat.chat_id, isMute: true });
          break;
        case 'Unmute Chat':
          self.$store.dispatch('Chat/mute_group', { chat_id: self.selectedChat.chat_id, isMute: false });
          break;
        case 'Delete Chat':
          // eslint-disable-next-line no-case-declarations
          const chat_id = self.selectedChat.chat_id;
          self.selectedChatId = "";
          self.$store.dispatch('Chat/delete_conversation', chat_id);
          self.selectedConversation({});
          self.setSelectedType('empty');
          break;
        case 'Delete Group':
          self.showDeleteGroupDialog = true;
          break;
        default:
          break;
      }

    },
    checkUserSelected(user) {
      if (this.removeParticipantMode) {
        const name = (user.first_name || '') + ' ' + (user.last_name || '');
        if (confirm('Remove ' + name.trim() + ' from this conversation?')) {
          this.$store.dispatch('Chat/remove_participant', {
            chat_id: this.selectedChatId,
            user_id: user.owner_id,
          }).then(() => {
            this.drawerParticipants = false;
            this.removeParticipantMode = false;
          });
        }
        return;
      }
      if (this.new_chat_mode === 'single' && this.typeSelected !== 'contact') {
        this.drawerParticipants = false;
        this.selected = [user];
        this.end();
      }
    },
    onClickChatMenu(item) {
      const self = this;
      self.new_chat_mode = '';
      switch (item.title) {
        case 'New Chat': {
          self.new_chat_mode = 'single';
          self.get_users().then(() => {
            self.scrollable('vsParticipants', "0%");
          });
          self.selected = [];
          self.drawerParticipants = !self.drawerParticipants;
          break;
        }
        case 'New Group': {
          self.new_chat_mode = 'group';
          self.get_users().then(() => {
            self.scrollable('vsParticipants', "0%");
          });
          self.selected = [];
          self.drawerParticipants = !self.drawerParticipants;
          break;
        }
        default: {
          self.route.push(item.route);
          break;
        }
      }
    },
    getAddressData(addressData, placeResultData) {
      const self = this;
      if (addressData && placeResultData) {
        self.valid = true;
        self.address = {
          chat_id: self.selectedChat.chat_id,
          description: placeResultData.formatted_address,
          latitude: addressData.latitude,
          longitude: addressData.longitude,
          url: placeResultData.url
        };
      }
    },
    getAddressEventData(addressData, placeResultData) {
      const self = this;
      if (addressData && placeResultData) {
        self.valid = true;
        self.event.chat_id = self.selectedChat.chat_id;
        self.event.address = placeResultData.formatted_address;
        self.event.url = placeResultData.url;
        self.event.latitude = addressData.latitude;
        self.event.longitude = addressData.longitude;
      }
    },
    setSelectedType(type) {
      const self = this;
      var textBar = document.getElementById('text-bar');
      self.typeSelected = type;
      self.classArray = [];
      self.overlay_ontheway = false;
      self.drawerParticipants = false;
      if (textBar) {
        textBar.classList.remove('display-event-options');
        textBar.classList.remove('display-update-options');
        textBar.classList.remove('display-invoice-options');
      }
      self.valid = false;
      self.messageContent = {};
      self.messageToEdit = null;

      if (self.typeSelected === 'text' || self.typeSelected === 'check_selected') {
        self.classArray.push('display-text-options');
        self.text_hash = '#';
      }
      this.displayOptionsText = false;
      switch (self.typeSelected) {
        case 'empty':
          break;
        case 'check_selected':
          self.typeSelected = 'text';
          self.classArray.push('display-text-options');
          break;
        case 'text':
          break;
        case 'address':
          self.classArray.push('display-text-options');
          break;
        case 'event':
          self.classArray.push('display-event-options');
          if (textBar) textBar.classList.add('display-event-options');
          break;
        case 'update':
          self.classArray.push('display-update-options');
          if (textBar) textBar.classList.add('display-update-options');
          break;
        case 'contact':
          self.classArray.push('display-text-options');
          self.get_users();
          self.drawerParticipants = true;
          self.selected = [];
          break;
        case 'invoice':
          self.classArray.push('display-invoice-options');
          if (textBar) textBar.classList.add('display-invoice-options');
          self.text_hash = 'X';
          break;
        case 'ontheway':
          self.overlay_ontheway = true;
          break;
        default:
          self.classArray.push('display-text-options');
          break;

      }
    },
    geolocate() {
      const self = this;
      navigator.geolocation.getCurrentPosition(position => {
        self.infoRoute.start.latitude = position.coords.latitude;
        self.infoRoute.start.longitude = position.coords.longitude;
      });
    },
    generate_route(item) {
      this.routeSelected = item;
      this.overlay_map = true;
    },
    send_ontheway(infoRoute, info_route) {
      const self = this;
      self.overlay_map = !self.overlay_map;
      var d = new Date();
      d.setSeconds(info_route.duration.value);

      // Prepare the ontheway message
      let message = {
        address: infoRoute.address,
        duration: info_route.duration,
        latitude: infoRoute.end.latitude,
        longitude: infoRoute.end.longitude,
        chat_id: infoRoute.chat_id,
        address_message_id: infoRoute.message_id,
        estimated_time_arrival: d,
        type: 'ontheway',
      };

      // Send directly since ontheway doesn't need UI editing
      self.dataToSend(message);

    },
    message_contact(contact) {
      const self = this;
      let toCreate = [];
      toCreate.push(contact);

      let participant_ids = [];
      for (const prop in toCreate) {
        participant_ids.push(toCreate[prop].owner_id);
      }

      self.$store.commit("Chat/status_contact", "sending-contact");
      self.$store.dispatch("Chat/get_chat", { participant_ids, chat_mode: self.new_chat_mode }).then(response => {
        if (response) {
          self.selectedConversation(self.xconversations.find(x => x.chat_id === response));
          self.$store.commit("Chat/status_contact", "");
        } else {
          self.$store.dispatch("Chat/new_chat", {
            title: '',
            participants: toCreate
          }).then(response => {
            self.selectedConversation(response);
            self.$store.commit("Chat/status_contact", "");
          });
        }
      });

    },
    add_contact(contact) {
      const self = this;
      self.$store.dispatch("Chat/add_contact", { user_id: contact.user_id });
    },
    scrollable(ref, percent) {
      const self = this;
      try {
        if (self.$refs[ref]) {
          self.$refs[ref].scrollTo(
            {
              y: percent
            },
            0
          );
        }
      } catch (e) {
        // scroll ref not available
      }

    },
    urlify(text) {
      const links = linkify.find(text);
      let urls = [];
      if (links.length > 0) {
        for (const link of links) {
          if (link.type === "url") {
            urls.push(link.href);
          }
        }
      }
      return urls;
    },
    verifyUrl(url) {
      if (url.indexOf("http") === -1) {
        return "http://" + url;
      }
      return url;
    },
    async create_image_from_pdf(item) {

      if (item.sending_status === 'generated_pdf') {
        alert('We are generating the invoice, please wait ...');
      } else {
        if (item.invoice.invoice_ready) {
          let url_pdf = item.invoice.url_invoice;
          this.showInvoiceModal = true;
          this.url_temp_invoice = url_pdf;
          this.sender_profile_picture_invoice = item.invoice.sender.profile_picture;
          let context;
          try {
            document.querySelector("#page-loader").innerHTML = 'Please wait while we generate the preview ...';
          } catch (error) {
            //
          }
          await pdfjsLib.getDocument({ url: url_pdf }).then(async (res) => {
            let _CANVAS = document.querySelector('#pdf-canvas');
            if (!_CANVAS) return;
            context = _CANVAS.getContext('2d');
            context.clearRect(0, 0, _CANVAS.width, _CANVAS.height);
            let page = await res.getPage(1);
            let pdf_original_width = page.getViewport(1).width;

            // as the canvas is of a fixed width we need to adjust the scale of the viewport where page is rendered
            let scale_required = _CANVAS.width / pdf_original_width;

            // get viewport to render the page at required scale
            let viewport = page.getViewport(scale_required);
            // set canvas height same as viewport height
            _CANVAS.height = viewport.height;
            let render_context = {
              canvasContext: context,
              viewport: viewport
            };
            await page.render(render_context);
            const canvas = document.querySelector("#pdf-canvas");
            const loader = document.querySelector("#page-loader");
            if (canvas) canvas.style.display = 'block';
            if (loader) loader.style.display = 'none';


          }).catch(() => {
            const loader = document.querySelector("#page-loader");
            if (loader) loader.innerHTML = 'Cannot preview this document ...';
          });
        } else {
          alert('We are generating the invoice, please wait ...');
          // this.showInvoiceReadyModal = true;
          //   this.url_temp_invoice = url_pdf;
          //   this.sender_profile_picture_invoice = sender_profile_picture;
        }
      }
    },
    hideInvoiceModal() {
      this.showInvoiceModal = false;
    },
    hideInvoiceReadyModal() {
      this.showInvoiceReadyModal = false;
    },
  }
  ,
}